// src/pages/tasks/TaskDetails.tsx
import React, {
  useEffect,
  useState,
  ChangeEvent,
  useMemo,
  useRef,
} from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";

import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  TextField,
  Button,
  Tooltip,
  Chip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  SelectChangeEvent,
  Collapse,
  Backdrop,
  CardActionArea,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Checkbox,
  Badge,
  Menu,
  Switch,
  Slider,
  LinearProgress,
  Fade,
  Grid,
  Stack,
  AvatarGroup,
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";

import DeleteIcon from "@mui/icons-material/Delete";
import AddTaskIcon from "@mui/icons-material/AddTask";
import PublicIcon from "@mui/icons-material/Public";
import PublicOffIcon from "@mui/icons-material/PublicOff";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PieChartIcon from "@mui/icons-material/PieChart";
import HistoryIcon from "@mui/icons-material/History";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import ViewListOutlinedIcon from "@mui/icons-material/ViewListOutlined";
import ViewKanbanOutlinedIcon from "@mui/icons-material/ViewKanbanOutlined";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GroupIcon from "@mui/icons-material/Group";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import TimelapseIcon from "@mui/icons-material/Timelapse";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import FlagIcon from "@mui/icons-material/Flag";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LabelIcon from "@mui/icons-material/Label";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SpeedIcon from "@mui/icons-material/Speed";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PrintIcon from "@mui/icons-material/Print";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TimelineIcon from "@mui/icons-material/Timeline";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import HomeIcon from "@mui/icons-material/Home";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";

import { baseUrl } from "../../constants";
// import { getTaskAssignmentsByStep } from "../../api/client"; // no longer needed
import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import { TaskRemindIn } from "../../enums/task_remind_in.enum";
import {
  getImportanceContainerColor,
  getImportanceContainerText,
} from "../../utils/task-importance.utils";
import {
  getTaskStatusContainerBgColor,
  getTaskStatusContainerTextColor,
  getTaskStatusLabel,
} from "../../utils/task-status.utils";
import { handleDate, handleDateWithTime } from "../../utils/date-handlers";
import { getUserInfoFromToken } from "../../utils/Main";

/* ------------------------------------------------------------------ */
/* Animations & Styled Components                                     */
/* ------------------------------------------------------------------ */
import { keyframes, styled } from "@mui/material/styles";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const GlassCard = styled(Card)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : alpha("#ffffff", 0.95),
  backdropFilter: "saturate(180%) blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  borderRadius: 16,
  boxShadow:
    theme.palette.mode === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.4)"
      : "0 8px 32px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 12px 48px rgba(0, 0, 0, 0.5)"
        : "0 12px 48px rgba(0, 0, 0, 0.12)",
  },
}));

const StyledIconButton = styled(IconButton)(() => ({
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.1)",
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 600,
  padding: "10px 24px",
  boxShadow: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? "0 8px 24px rgba(0, 0, 0, 0.4)"
        : "0 8px 24px rgba(0, 0, 0, 0.15)",
  },
}));

const StatusChip = styled(Chip)(() => ({
  borderRadius: 8,
  fontWeight: 700,
  fontSize: "0.813rem",
  height: 32,
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const PriorityBadge = styled(Box)(() => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 16px",
  borderRadius: 20,
  fontWeight: 700,
  fontSize: "0.875rem",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 12,
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));


const FloatingActionButton = styled(IconButton)(({ theme }) => ({
  position: "fixed",
  bottom: 24,
  right: 24,
  width: 56,
  height: 56,
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  zIndex: 1000,
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
    transform: "scale(1.1) rotate(90deg)",
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.3)",
  },
}));

const BreadcrumbLink = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  cursor: "pointer",
  color: theme.palette.text.secondary,
  transition: "all 0.2s ease",
  "&:hover": {
    color: theme.palette.primary.main,
  },
}));

const UpdateIndicator = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 80,
  right: 24,
  padding: "8px 16px",
  borderRadius: 8,
  background: alpha(theme.palette.warning.main, 0.95),
  color: "#fff",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  zIndex: 1100,
  animation: `${fadeIn} 0.3s ease`,
}));

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

interface User {
  id: number;
  username: string;
  imageUrl?: string;
  profile?: { imageUrl?: string | null };
}

interface Task {
  id: number;
  title: string;
  description: string;
  projectId: number;
  projectName: string;
  ref: string;
  startDate: string;
  endDate: string;
  importance: TaskImportance;
  status: TaskStatus;
  createdById: number;
  parentTaskId: number | null;
  createdBy: User;
  assignedUsers: User[];
  public: boolean;
  type?: string;

  // Visibility (enum in backend: PRIVATE | PROJECT | DEPARTMENT | WORKSPACE)
  visibility?: "PRIVATE" | "PROJECT" | "DEPARTMENT" | "WORKSPACE" | string | null;

  // Account fields
  accountId?: number | null;
  accountName?: string | null;
  accountSlug?: string | null;
  accountEmail?: string | null;
  accountDomain?: string | null;

  // New advanced fields
  archived?: boolean;
  estimateMinutes?: number | null;
  spentMinutes?: number | null;
  points?: number | null;
  progress?: number | null; // 0–100
  tags?: string[];

  // approvals summary
  approvalStatus?: "NOT_REQUIRED" | "PENDING" | "APPROVED" | "REJECTED" | "CANCELLED" | null;
  currentApprovalStep?: number | null;

  remindIn?: TaskRemindIn | string | null;
  remindAt?: string | null;
}

interface Comment {
  id: number;
  text: string;
  imageUrl: string | null;
  taskId: number;
  date: string;
  user: User;
}

interface FileWithTitle {
  file: File;
  title: string;
}

interface TaskStatusHistory {
  id: number;
  newStatus: string;
  date: Date;
}

interface TaskAttachment {
  id: number;
  title: string;
  url: string;
  taskId: number;
}

/** extra subtask shape returned by backend include */
interface SubtaskExtra {
  id: number;
  title: string;
  description?: string | null;
  status: TaskStatus;
  startDate?: string | null;
  endDate?: string | null;
  importance?: TaskImportance | null;
  createdBy?: {
    id: number;
    username: string;
    profile?: { imageUrl?: string | null };
  };
  project?: { id: number; name: string | null };
}

/** Parent task preview */
interface ParentPreview {
  id: number;
  title: string;
  createdBy: User;
}

interface Follower {
  id: number;
  username: string;
  imageUrl?: string | null;
  name?: string | null;
}

/* -------- Approvals: payload from /tasks/:id/approvals ---------- */
type ApprovalRequiredStrategy = "ALL" | "ANY";
type ApprovalStatus =
  | "PENDING"
  | "APPROVED"
  | "REJECTED"
  | "SKIPPED"
  | "CANCELLED";

interface ApprovalStepDefinition {
  id: number;
  stepOrder: number;
  actorType: string;
  requiredStrategy: ApprovalRequiredStrategy;
}

interface TaskApprovalItem {
  id: number;
  stepDefId: number;
  status: ApprovalStatus;
  actedAt?: string | null;
  comment?: string | null;
  approver?: {
    id: number;
    username: string;
    profile?: { imageUrl?: string | null };
  } | null;
  stepDef?: ApprovalStepDefinition;
}

interface TaskApprovalsPayload {
  policy: {
    id: number;
    name: string;
    taskType: string;
    version: number;
    isActive: boolean;
    steps: ApprovalStepDefinition[];
  } | null;
  approvals: TaskApprovalItem[];
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

type SubTaskView = "grid" | "list" | "board";
type SubTaskSort = "createdDesc" | "createdAsc" | "updatedDesc" | "updatedAsc";

/* RTL helper for Arabic/Hebrew etc */
const isRTL = (txt?: string | null) => !!txt && /[\u0590-\u08FF]/.test(txt); // Arabic/Hebrew ranges

/* Consistent single-line truncation that aligns from the left. */
const leftAlignedSingleLine = (txt?: string | null) => ({
  direction: isRTL(txt) ? "rtl" : "ltr",
  textAlign: "left" as const,
  whiteSpace: "nowrap" as const,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
});

/* Multi-line clamp that still keeps alignment from the left. */
const leftAlignedClamp = (lines: number, txt?: string | null) => ({
  direction: isRTL(txt) ? "rtl" : "ltr",
  textAlign: "left" as const,
  overflow: "hidden" as const,
  textOverflow: "ellipsis" as const,
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical" as const,
});

const STORAGE_KEYS = {
  subTaskView: "td_sub_view",
  subTaskSort: "td_sub_sort",
  statusFilter: "td_sub_status_filter",
  priorityFilter: "td_sub_priority_filter",
  subExpanded: "td_sub_expanded",
};

// Keep visibility options in sync with backend enum (excluding PUBLIC which you toggle separately)
const VISIBILITY_OPTIONS = ["PRIVATE", "PROJECT", "DEPARTMENT", "WORKSPACE"] as const;

const TaskDetails: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // auth
  const token = localStorage.getItem("accessToken");
  const me = getUserInfoFromToken();
  const userId = me?.id;
  const role = me?.role;

  // core state
  const [task, setTask] = useState<Task | null>(null);
  const [parentTask, setParentTask] = useState<ParentPreview | null>(null);
  const [subTasks, setSubTasks] = useState<SubtaskExtra[]>([]);
  const [taskHistory, setTaskHistory] = useState<TaskStatusHistory[]>();
  const [taskAttachments, setTaskAttachments] = useState<TaskAttachment[]>();

  // approvals
  const [approvalsData, setApprovalsData] = useState<TaskApprovalsPayload | null>(null);
  const [loadingApprovals, setLoadingApprovals] = useState(false);

  // ux state
  const [fetchingTask, setFetchingTask] = useState(false);
  const [fetchingComments, setFetchingComments] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // comments
  const [comments, setComments] = useState<Comment[]>([]);
  const [_, setAddCommentOpen] = useState(false);
  const [newCommentText, setNewCommentText] = useState<string>("");
  const [newCommentImage, setNewCommentImage] = useState<File | null>(null);
  const [newCommentImageSrc, setnewCommentImageSrc] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<number>();
  const [openImageCommentId, setOpenImageCommentId] = useState<number | null>(null);

  // follow + visibility + delete
  const [isFollowed, setIsFollowed] = useState(false);
  const [isCheckingFollow, setIsCheckingFollow] = useState(true);
  const [deleteTaskOpen, setDeleteTaskOpen] = useState(false);

  // visibility menu
  const [visAnchorEl, setVisAnchorEl] = useState<HTMLElement | null>(null);
  const visMenuOpen = Boolean(visAnchorEl);

  // archived toggle working state
  const [archivedSaving, setArchivedSaving] = useState(false);

  // followers dialog
  const [followersOpen, setFollowersOpen] = useState(false);
  const [followers, setFollowers] = useState<Follower[]>([]);
  const [followersLoading, setFollowersLoading] = useState(false);
  const [followersCount, setFollowersCount] = useState<number>(0);

  // attachments
  const [attachmentOpen, setAttachmentOpen] = useState(false);
  const [newAttachments, setNewAttachments] = useState<FileWithTitle[]>([]);
  const [addingAttachments, setAddingAttachments] = useState(false);
  const [selectedAttachmentImageUrl, setSelectedAttachmentImageUrl] = useState<string | null>(null);
  const [attachmentToDelete, setAttachmentToDelete] = useState<TaskAttachment>();

  // sections/dialogs
  const [descExpanded, setDescExpanded] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // sub tasks view + sort + filters
  const [subTasksExpanded, setSubTasksExpanded] = useState(true);
  const [subTaskView, setSubTaskView] = useState<SubTaskView>("grid");
  const [subTaskSort, setSubTaskSort] = useState<SubTaskSort>("createdDesc");
  const [statusFilter, setStatusFilter] = useState<TaskStatus[]>([]);
  const [priorityFilter, setPriorityFilter] = useState<TaskImportance[]>([]);

  // title + assignees UX
  const [titleExpanded, setTitleExpanded] = useState(false);

  // assignments by step
  const [assignmentsByStep, setAssignmentsByStep] = useState<any[]>([]);
  const [loadingAssignments] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState<{ [key: string]: boolean }>({});
  
  // Initialize expandedDescriptions to true by default when assignmentsByStep changes
  useEffect(() => {
    if (assignmentsByStep.length > 0 && userId) {
      const newExpanded: { [key: string]: boolean } = {};
      // Filter to only current user's assignments (matching the render logic)
      const currentUserAssignments = assignmentsByStep.flatMap((stepData: any) =>
        (stepData.assignees?.filter((assignee: any) => assignee?.id === userId) || []).map((assignee: any) => ({
          ...assignee,
          stepOrder: stepData.stepOrder,
          stepId: stepData.stepId,
        }))
      );
      
      // Set all userDescriptions to expanded by default
      currentUserAssignments.forEach((assignee: any, idx: number) => {
        if (assignee?.userDescription) {
          const uniqueKey = `current-user-${idx}`;
          newExpanded[uniqueKey] = true; // Default to expanded
        }
      });
      setExpandedDescriptions((prev) => ({ ...prev, ...newExpanded }));
    }
  }, [assignmentsByStep, userId]);
  
  // Status change comment dialog
  const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ taskId: number; userId: number; newStatus: string } | null>(null);
  const [statusChangeComment, setStatusChangeComment] = useState("");

  // tags (edit)
  const [tagsInput, setTagsInput] = useState("");
  const [savingTags, setSavingTags] = useState(false);

  // Advanced numbers edit (progress moved to its own card)
  const [savingAdvanced, setSavingAdvanced] = useState(false);
  const [estimateMinutesDraft, setEstimateMinutesDraft] = useState<number | ''>('');
  const [spentMinutesDraft, setSpentMinutesDraft] = useState<number | ''>('');
  const [pointsDraft, setPointsDraft] = useState<number | ''>('');

  // Progress (standalone section)
  const [progressDraft, setProgressDraft] = useState<number>(0);
  const [progressSaving, setProgressSaving] = useState(false);

  // Reminder dialog
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [reminderDatetime, setReminderDatetime] = useState<string>(""); // yyyy-MM-ddTHH:mm
  const [savingReminder, setSavingReminder] = useState(false);

  // carousel ref
  const scrollRef = useRef<HTMLDivElement>(null);

  // project membership (to allow creating sub-tasks)
  const [projectMemberLoading, setProjectMemberLoading] = useState(false);
  const [isProjectMember, setIsProjectMember] = useState<boolean>(false);

  // New Enhancement States
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [quickActionAnchor, setQuickActionAnchor] = useState<null | HTMLElement>(null);
  const quickActionOpen = Boolean(quickActionAnchor);
  const [showUpdateIndicator] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);

  /* --------------------------- helpers & computed --------------------------- */

  const isLongTitle = useMemo(() => (task?.title?.length || 0) > 60, [task?.title]);

  const statusOptions = useMemo(
    () => Object.values(TaskStatus).filter((s) => s !== (TaskStatus as any).ALL) as TaskStatus[],
    []
  );
  const priorityOptions = useMemo(() => Object.values(TaskImportance) as TaskImportance[], []);

  // Filtering -> Sorting pipeline for subtasks
  const filteredSubtasks = useMemo(() => {
    let arr = [...subTasks];
    if (statusFilter.length) {
      arr = arr.filter((t) => statusFilter.includes(t.status));
    }
    if (priorityFilter.length) {
      arr = arr.filter((t) => (t.importance ? priorityFilter.includes(t.importance) : false));
    }
    return arr;
  }, [subTasks, statusFilter, priorityFilter]);

  const sortedSubtasks = useMemo(() => {
    const copy = [...filteredSubtasks];

    const getCreated = (t: SubtaskExtra) =>
      t.startDate ? new Date(t.startDate).getTime() : t.id; // fallback proxy
    const getUpdated = (t: SubtaskExtra) =>
      t.endDate ? new Date(t.endDate).getTime() : t.startDate ? new Date(t.startDate).getTime() : t.id;

    switch (subTaskSort) {
      case "createdAsc":
        return copy.sort((a, b) => getCreated(a) - getCreated(b));
      case "updatedDesc":
        return copy.sort((a, b) => getUpdated(b) - getUpdated(a));
      case "updatedAsc":
        return copy.sort((a, b) => getUpdated(a) - getUpdated(b));
      case "createdDesc":
      default:
        return copy.sort((a, b) => getCreated(b) - getCreated(a));
    }
  }, [filteredSubtasks, subTaskSort]);

  const isSubtask = task?.parentTaskId != null;

  const groupedSubtasksByStatus = useMemo(() => {
    const groups: Record<string, SubtaskExtra[]> = {};
    (filteredSubtasks || []).forEach((t) => {
      const key = String(t.status);
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  }, [filteredSubtasks]);
  const totalSubtasks = subTasks?.length || 0;
  const hasSubtasks = totalSubtasks > 0;
  const subtaskFiltersApplied = statusFilter.length > 0 || priorityFilter.length > 0;
  const hasFilteredSubtasks = sortedSubtasks.length > 0;

  // Approvals grouped by stepOrder
  const approvalsByStep = useMemo(() => {
    if (!approvalsData?.policy) return [];
    const stepsSorted = [...approvalsData.policy.steps].sort((a, b) => a.stepOrder - b.stepOrder);
    return stepsSorted.map((step) => {
      const items = (approvalsData.approvals || []).filter((a) => a.stepDefId === step.id);
      return { step, items };
    });
  }, [approvalsData]);

  const approvalOverallLabel = useMemo(() => {
    switch (task?.approvalStatus) {
      case "APPROVED":
        return { label: "Approved", icon: <CheckCircleIcon fontSize="small" />, color: theme.palette.success.main };
      case "PENDING":
        return { label: "Pending", icon: <PendingIcon fontSize="small" />, color: theme.palette.warning.main };
      case "REJECTED":
        return { label: "Rejected", icon: <DoNotDisturbOnIcon fontSize="small" />, color: theme.palette.error.main };
      case "CANCELLED":
        return { label: "Cancelled", icon: <CloseIcon fontSize="small" />, color: theme.palette.text.secondary };
      case "NOT_REQUIRED":
        return { label: "Not Required", icon: <CheckCircleIcon fontSize="small" />, color: theme.palette.text.secondary };
      default:
        return null;
    }
  }, [task?.approvalStatus, theme.palette]);

  // Helper function to get user task status display
  const getUserTaskStatusDisplay = (status: string) => {
    switch (status) {
      case "TODO":
        return { 
          label: "To Do", 
          icon: <HourglassBottomIcon fontSize="small" />, 
          color: theme.palette.grey[500] 
        };
      case "IN_PROGRESS":
        return { 
          label: "In Progress", 
          icon: <TimelapseIcon fontSize="small" />, 
          color: theme.palette.warning.main 
        };
      case "DONE":
        return { 
          label: "Done", 
          icon: <CheckCircleIcon fontSize="small" />, 
          color: theme.palette.success.main 
        };
      case "POSTPONED":
        return { 
          label: "Postponed", 
          icon: <DoNotDisturbOnIcon fontSize="small" />, 
          color: theme.palette.error.main 
        };
      default:
        return { 
          label: "Done", 
          icon: <CheckCircleIcon fontSize="small" />, 
          color: theme.palette.success.main 
        };
    }
  };

  /* -------------------------------- effects -------------------------------- */

  // Load persisted controls on first mount
  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEYS.subTaskView);
      if (v === "grid" || v === "list" || v === "board") setSubTaskView(v);
      const s = localStorage.getItem(STORAGE_KEYS.subTaskSort);
      if (s === "createdDesc" || s === "createdAsc" || s === "updatedDesc" || s === "updatedAsc")
        setSubTaskSort(s);
      const st = localStorage.getItem(STORAGE_KEYS.statusFilter);
      if (st) setStatusFilter(JSON.parse(st));
      const pf = localStorage.getItem(STORAGE_KEYS.priorityFilter);
      if (pf) setPriorityFilter(JSON.parse(pf));
      const ex = localStorage.getItem(STORAGE_KEYS.subExpanded);
      if (ex != null) setSubTasksExpanded(ex === "true");
    } catch {
      // ignore parsing errors
    }
  }, []);

  // Persist controls whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.subTaskView, subTaskView);
  }, [subTaskView]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.subTaskSort, subTaskSort);
  }, [subTaskSort]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.statusFilter, JSON.stringify(statusFilter));
  }, [statusFilter]);

  // Create a memoized key from user statuses to detect changes
  const userStatusesKey = useMemo(() => {
    return assignmentsByStep
      .flatMap((step: any) => step.assignees || [])
      .map((assignee: any) => `${assignee.id}:${assignee.userTaskStatus || "DONE"}`)
      .sort()
      .join(",");
  }, [assignmentsByStep]);

  // Auto-update task status based on all user statuses when assignments change
  useEffect(() => {
    if (assignmentsByStep.length > 0 && task && id && userStatusesKey) {
      updateTaskStatusBasedOnUserStatuses(assignmentsByStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStatusesKey, task?.id]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.priorityFilter, JSON.stringify(priorityFilter));
  }, [priorityFilter]);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.subExpanded, String(subTasksExpanded));
  }, [subTasksExpanded]);

  useEffect(() => {
    setShowSkeleton(true);
    Promise.all([
      fetchTaskDetails(),
      fetchComments(),
      checkFollow(),
      fetchTaskAttachments(),
      fetchTaskSubTasks(),
      getFollowersList(), // load initial follower list + count
      // fetchAssignmentsByStep(), // load assignments grouped by step (now derived in fetchTaskDetails)
    ]).finally(() => {
      // Hide skeleton after all data is loaded
      setTimeout(() => setShowSkeleton(false), 300);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // refresh approvals after we have task (and when id changes)
    if (id) fetchTaskApprovals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check if current user is member of the task's project (for creating sub-tasks)
  useEffect(() => {
    const checkProjectMembership = async () => {
      if (!token || !task?.projectId || !userId) {
        setIsProjectMember(false);
        return;
      }
      setProjectMemberLoading(true);
      try {
        const res = await axios.get(`${baseUrl}/projects/project-users/${task.projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const arr: any[] = Array.isArray(res.data) ? res.data : [];
        const member = arr.some((row) => {
          const u = row?.user ?? row;
          const uid = Number(u?.id ?? row?.id ?? row?.userId);
          return Number(uid) === Number(userId);
        });
        setIsProjectMember(member);
      } catch {
        setIsProjectMember(false);
      } finally {
        setProjectMemberLoading(false);
      }
    };
    checkProjectMembership();
  }, [task?.projectId, token, userId]);

  /* ---------------------------------- api ---------------------------------- */

  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchTaskDetails = async () => {
    try {
      setFetchingTask(true);
      const { data } = await axios.get<Task>(`${baseUrl}/tasks/${id}`, {
        headers: authHeader,
      });
      // Extract account info from nested object if not already flat
      const accountData = (data as any).account;
      if (accountData) {
        (data as any).accountId = accountData.id;
        (data as any).accountName = accountData.name;
        (data as any).accountEmail = accountData.email;
        (data as any).accountDomain = accountData.domain;
        (data as any).accountSlug = accountData.slug;
      }
      
      // Extract project info from nested object if not already flat
      const projectData = (data as any).project;
      if (projectData) {
        (data as any).projectId = projectData.id;
        (data as any).projectName = projectData.name;
      }
      
      console.log("🏢 DATA EXTRACTED:", {
        accountId: (data as any).accountId,
        accountName: (data as any).accountName,
        projectId: (data as any).projectId,
        projectName: (data as any).projectName,
      });
      setTask(data);

      // Fetch raw UserTask data with proper status
      let rawUserTasks: any[] = [];
      try {
        const { data: userTaskData } = await axios.get(`${baseUrl}/tasks/usertasks-raw/${id}`, {
          headers: authHeader,
        });
        rawUserTasks = Array.isArray(userTaskData) ? userTaskData : [];
      } catch (e: any) {
        // Silently fall back to assignedUsers from main response
        rawUserTasks = [];
      }

      // Build assignments by step from raw UserTask data (if available) or task.assignedUsers
      try {
        const groups = new Map<number, any>();
        const assigned = rawUserTasks.length > 0 ? rawUserTasks : ((data as any)?.assignedUsers || []);
        
        assigned.forEach((au: any) => {
          // Handle both flat (old DTO) and nested (new DTO) structures
          const isFlat = au.username && !au.user;
          const user = isFlat ? au : au?.user;
          const step = au?.assignmentStep || null;
          const stepId = step?.id ?? -1;
          
          if (!groups.has(stepId)) {
            groups.set(stepId, {
              stepId,
              stepOrder: step?.stepOrder ?? 1,
              assignmentType: step?.assignmentType ?? "USER",
              targetUserId: step?.targetUserId ?? null,
              targetRole: step?.targetRole ?? null,
              targetPositionId: step?.targetPositionId ?? null,
              targetPosition: step?.targetPosition ?? null,
              targetUser: step?.targetUser ?? null,
              assignees: [],
            });
          }
          
          groups.get(stepId).assignees.push({
            id: user?.id,
            username: user?.username,
            imageUrl: user?.profile?.imageUrl || user?.imageUrl || null,
            assignmentStatus: "ASSIGNED",
            userTaskStatus: au?.status || "DONE",
            comment: au?.comment || null,
            userDescription: au?.userDescription,
            actedAt: au?.actedAt,
          });
        });
        
        const arr = Array.from(groups.values()).sort((a, b) => a.stepOrder - b.stepOrder);
        setAssignmentsByStep(arr);
      } catch (err) {
        console.error('[TaskDetails] Error building assignmentsByStep:', err);
        setAssignmentsByStep([]);
      }

      // prepare drafts for advanced fields
      setEstimateMinutesDraft(typeof data.estimateMinutes === "number" ? data.estimateMinutes : "");
      setSpentMinutesDraft(typeof data.spentMinutes === "number" ? data.spentMinutes : "");
      setPointsDraft(typeof data.points === "number" ? data.points : "");
      setProgressDraft(typeof data.progress === "number" ? data.progress : 0);

      // parent preview
      if (data?.parentTaskId) {
        try {
          const { data: parent } = await axios.get<Task>(`${baseUrl}/tasks/${data.parentTaskId}`, {
            headers: authHeader,
          });
          setParentTask({
            id: parent.id,
            title: parent.title,
            createdBy: parent.createdBy,
          });
        } catch {
          setParentTask(null);
        }
      } else {
        setParentTask(null);
      }

      await getTaskHistory();
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Failed To Fetch Task : ${err?.response?.data?.message || err.message}`,
        severity: "error",
      });
    } finally {
      setFetchingTask(false);
    }
  };

  const fetchTaskApprovals = async () => {
    try {
      setLoadingApprovals(true);
      const { data } = await axios.get<TaskApprovalsPayload>(
        `${baseUrl}/tasks/${id}/approvals`,
        { headers: authHeader }
      );
      setApprovalsData(data);
    } catch {
      setApprovalsData(null);
    } finally {
      setLoadingApprovals(false);
    }
  };

  const getTaskHistory = async () => {
    try {
      const { data } = await axios.get<TaskStatusHistory[]>(`${baseUrl}/tasks/history/${id}`, {
        headers: authHeader,
      });
      setTaskHistory(data);
    } catch {
      // non-blocking
    }
  };

  const fetchComments = async () => {
    try {
      setFetchingComments(true);
      const { data } = await axios.get<Comment[]>(`${baseUrl}/task-comment/task/${id}`, {
        headers: authHeader,
      });
      setComments(data);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Failed To Fetch Comment ${err?.response?.data?.message || err.message}`,
        severity: "error",
      });
    } finally {
      setFetchingComments(false);
    }
  };

  const checkFollow = async () => {
    try {
      const { data } = await axios.get<{ follow: boolean }>(
        `${baseUrl}/tasks/check-follow/${id}`,
        { headers: authHeader }
      );
      setIsFollowed(data.follow);
    } catch {
      // ignore
    } finally {
      setIsCheckingFollow(false);
    }
  };

  const getFollowersList = async () => {
    try {
      setFollowersLoading(true);
      const { data } = await axios.get<Follower[]>(`${baseUrl}/tasks/${id}/followers`, {
        headers: authHeader,
      });
      const list = (data || []).map((u) => ({
        id: u.id,
        username: u.username,
        imageUrl: (u as any).profile?.imageUrl ?? u.imageUrl ?? null,
        name: (u as any).profile?.name ?? null,
      }));
      setFollowers(list);
      setFollowersCount(list.length);
    } catch {
      setFollowers([]);
      setFollowersCount(0);
    } finally {
      setFollowersLoading(false);
    }
  };

  const fetchTaskAttachments = async () => {
    try {
      const { data } = await axios.get<TaskAttachment[]>(
        `${baseUrl}/tasks/attachments/${id}`,
        { headers: authHeader }
      );
      setTaskAttachments(data);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Failed To Fetch Attachemnts ${err?.response?.data?.message || err.message}`,
        severity: "error",
      });
    }
  };

  const fetchTaskSubTasks = async () => {
    try {
      const { data } = await axios.get<SubtaskExtra[]>(`${baseUrl}/tasks/sub-tasks/${id}`, {
        headers: authHeader,
      });
      setSubTasks(data || []);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: `Failed To Fetch Attachemnts ${err?.response?.data?.message || err.message}`,
        severity: "error",
      });
    }
  };

  const changePrivacy = async () => {
    try {
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { public: !task?.public },
        { headers: authHeader }
      );
      fetchTaskDetails();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Update Task ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  const updateVisibility = async (newVis: string) => {
    try {
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { visibility: newVis },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, visibility: newVis } : prev));
      setSnackbar({ open: true, message: "Visibility updated", severity: "success" });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Update Visibility ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setVisAnchorEl(null);
    }
  };

  const handleStatusChange = async (event: SelectChangeEvent) => {
    const newStatus = event.target.value as TaskStatus;
    try {
      await axios.patch(
        `${baseUrl}/tasks/status/${id}`,
        { status: newStatus },
        { headers: authHeader }
      );
    } catch {
      // silent; refresh anyway
    } finally {
      fetchTaskDetails();
    }
  };

  // User task status options
  const userTaskStatusOptions = [
    { value: "TODO", label: "To Do" },
    { value: "IN_PROGRESS", label: "In Progress" },
    { value: "DONE", label: "Done" },
    { value: "POSTPONED", label: "Postponed" },
  ];

  // Method to check all user statuses and update task status accordingly
  const updateTaskStatusBasedOnUserStatuses = async (assignments: any[]) => {
    if (!task || !id) return;

    // Get all user statuses from all assignees
    const allUserStatuses = assignments
      .flatMap((step: any) => step.assignees || [])
      .map((assignee: any) => assignee.userTaskStatus || "DONE");

    // If no assignees, don't change task status
    if (allUserStatuses.length === 0) return;

    // Check if all user statuses are DONE
    const allDone = allUserStatuses.every((status: string) => status === "DONE");

    // Determine new task status
    const newTaskStatus = allDone ? TaskStatus.REVIEW : TaskStatus.TODO;

    // Only update if status is different
    if (task.status !== newTaskStatus) {
      try {
        await axios.patch(
          `${baseUrl}/tasks/status/${id}`,
          { status: newTaskStatus },
          { headers: authHeader }
        );
        
        // Update local task state
        setTask((prev) => (prev ? { ...prev, status: newTaskStatus } : prev));
      } catch (error: any) {
        console.error("Failed to auto-update task status:", error);
        // Silent fail - don't show error to user for auto-updates
      }
    }
  };

  // Handler for updating user task status - opens dialog first
  const handleUserTaskStatusChange = (
    taskId: number,
    userId: number,
    newStatus: string
  ) => {
    setPendingStatusChange({ taskId, userId, newStatus });
    setStatusChangeComment("");
    setStatusChangeDialogOpen(true);
  };

  // Confirm status change with comment
  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    try {
      await axios.patch(
        `${baseUrl}/tasks/usertask-status/${pendingStatusChange.taskId}/${pendingStatusChange.userId}`,
        { 
          status: pendingStatusChange.newStatus,
          comment: statusChangeComment || undefined,
        },
        { headers: authHeader }
      );
      
      // Update local state and check all user statuses
      setAssignmentsByStep((prev) => {
        const updated = prev.map((step: any) => ({
          ...step,
          assignees: step.assignees?.map((assignee: any) =>
            assignee.id === pendingStatusChange.userId
              ? { 
                  ...assignee, 
                  userTaskStatus: pendingStatusChange.newStatus,
                  comment: statusChangeComment || assignee.comment,
                }
              : assignee
          ),
        }));
        
        // Check all user statuses and update task status after state update
        setTimeout(() => {
          updateTaskStatusBasedOnUserStatuses(updated);
        }, 100);
        
        return updated;
      });

      setStatusChangeDialogOpen(false);
      setPendingStatusChange(null);
      setStatusChangeComment("");

      setSnackbar({
        open: true,
        message: "User status updated successfully!",
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed to update user status: ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  // Cancel status change
  const cancelStatusChange = () => {
    setStatusChangeDialogOpen(false);
    setPendingStatusChange(null);
    setStatusChangeComment("");
  };

  // Priority editing like status
  const handlePriorityChange = async (event: SelectChangeEvent) => {
    const newImportance = event.target.value as TaskImportance;
    try {
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { importance: newImportance },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, importance: newImportance } : prev));
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Update Priority ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  const handleArchivedToggle = async (checked: boolean) => {
    try {
      setArchivedSaving(true);
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { archived: checked },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, archived: checked } : prev));
      setSnackbar({
        open: true,
        message: `Task ${checked ? "archived" : "unarchived"}.`,
        severity: "success",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Update Archived ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setArchivedSaving(false);
    }
  };

  const saveAdvancedFields = async () => {
    try {
      setSavingAdvanced(true);
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        {
          estimateMinutes: estimateMinutesDraft === "" ? null : Number(estimateMinutesDraft),
          spentMinutes: spentMinutesDraft === "" ? null : Number(spentMinutesDraft),
          points: pointsDraft === "" ? null : Number(pointsDraft),
        },
        { headers: authHeader }
      );
      setTask((prev) =>
        prev
          ? {
              ...prev,
              estimateMinutes: estimateMinutesDraft === "" ? null : Number(estimateMinutesDraft),
              spentMinutes: spentMinutesDraft === "" ? null : Number(spentMinutesDraft),
              points: pointsDraft === "" ? null : Number(pointsDraft),
            }
          : prev
      );
      setSnackbar({ open: true, message: "Advanced fields saved.", severity: "success" });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Save Advanced ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setSavingAdvanced(false);
    }
  };

  // Progress save (now standalone section after Description)
  const saveProgress = async (value: number) => {
    try {
      setProgressSaving(true);
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { progress: value },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, progress: value } : prev));
    } catch {
      // optional toast
    } finally {
      setProgressSaving(false);
    }
  };

  const deleteTask = async () => {
    // extra guard: block deleting if parent has subtasks
    if (task?.parentTaskId === null && (subTasks?.length || 0) > 0) {
      setDeleteTaskOpen(false);
      setSnackbar({
        open: true,
        message: "This task has sub-tasks. Delete them first, then delete this task.",
        severity: "warning",
      });
      return;
    }

    try {
      await axios.delete(`${baseUrl}/tasks/${id}`, { headers: authHeader });
      setSnackbar({ open: true, message: "Task deleted successfully!", severity: "success" });
      setDeleteTaskOpen(false);
      navigate(-1);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Delete Task ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  /* -------------------------------- comments -------------------------------- */

  const handleCommentSubmit = async () => {
    const hasText = newCommentText.trim().length > 0;
    if (!hasText && !newCommentImage) return;
    setSubmitting(true);
    const formData = new FormData();
    formData.append("text", newCommentText);
    formData.append("taskId", id!.toString());
    if (newCommentImage) formData.append("image", newCommentImage);

    try {
      await axios.post(`${baseUrl}/task-comment`, formData, {
        headers: { "Content-Type": "multipart/form-data", ...authHeader },
      });
      setNewCommentText("");
      setNewCommentImage(null);
      setnewCommentImageSrc(null);
      setAddCommentOpen(false);
      setSnackbar({ open: true, message: "Comment Added successfully!", severity: "success" });
      fetchComments();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Create Comment ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCommentDeleteClick = (commentId: number) => {
    setCommentOpen(true);
    setCommentToDelete(commentId);
  };
  const handleConfirmCommentDelete = async () => {
    try {
      await axios.delete(`${baseUrl}/task-comment/${commentToDelete}`, { headers: authHeader });
      setCommentToDelete(undefined);
      setSnackbar({ open: true, message: "Comment Deleted successfully!", severity: "success" });
      fetchComments();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Delete Comment ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setCommentOpen(false);
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setNewCommentImage(event.target.files[0]);
      setnewCommentImageSrc(URL.createObjectURL(event.target.files[0]));
      event.target.value = "";
    }
  };
  const handleImageCancel = () => {
    setNewCommentImage(null);
    if (newCommentImageSrc) URL.revokeObjectURL(newCommentImageSrc);
    setnewCommentImageSrc(null);
  };

  /* ------------------------------ follow toggle ----------------------------- */

  const handleFollowToggle = async () => {
    try {
      setIsCheckingFollow(true);
      const endpoint = isFollowed ? "unfollow" : "follow";
      await axios.post(
        `${baseUrl}/tasks/${endpoint}`,
        { taskId: Number(id) },
        { headers: authHeader }
      );
      await checkFollow();
      await getFollowersList();
    } catch {
      // ignore
    } finally {
      setIsCheckingFollow(false);
    }
  };

  /* ------------------------------- attachments ------------------------------ */

  const handleAttachmentFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    const mapped: FileWithTitle[] = files.map((file) => ({ file, title: "" }));
    setNewAttachments((prev) => [...prev, ...mapped]);
  };
  const handleAttachmentTitleChange = (index: number, value: string) => {
    setNewAttachments((prev) => {
      const out = [...prev];
      out[index] = { ...out[index], title: value };
      return out;
    });
  };
  const handleAttachmentSend = async () => {
    if (!newAttachments.length) return;
    setAddingAttachments(true);
    const formData = new FormData();
    formData.append("taskId", id!.toString());
    newAttachments.forEach((a) => {
      formData.append("files", a.file);
      formData.append("titles", a.title);
    });

    try {
      await axios.post(`${baseUrl}/tasks/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data", ...authHeader },
      });
      setNewAttachments([]);
      setAttachmentOpen(false);
      fetchTaskAttachments();
    } catch {
      // toast handled elsewhere
    } finally {
      setAddingAttachments(false);
    }
  };

  const getFileType = (url: string) => {
    const parts = url.split(".");
    return parts[parts.length - 1].toLowerCase();
  };
  const isImage = (ext: string) => ["jpg", "jpeg", "png", "gif", "webp"].includes(ext);

  const handleAttachmentCardClick = (attachment: TaskAttachment) => {
    const ext = getFileType(attachment.url);
    if (isImage(ext)) {
      setSelectedAttachmentImageUrl(attachment.url);
    } else {
      const link = document.createElement("a");
      link.href = attachment.url;
      link.download = attachment.title || `file.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await axios.delete(`${baseUrl}/tasks/attachments/${attachmentId}`, {
        headers: authHeader,
      });
      setSnackbar({ open: true, message: "Attachment Deleted successfully!", severity: "success" });
      fetchTaskAttachments();
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Delete Attachment ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setAttachmentToDelete(undefined);
    }
  };

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: dir === "right" ? 240 : -240,
      behavior: "smooth",
    });
  };

  /* ------------------------------- tags edit -------------------------------- */

  const addTag = async () => {
    const cleaned = tagsInput.trim();
    if (!cleaned) return;
    const existing = task?.tags || [];
    if (existing.includes(cleaned)) {
      setTagsInput("");
      return;
    }
    try {
      setSavingTags(true);
      const newTags = [...existing, cleaned];
      await axios.patch(`${baseUrl}/tasks/${id}`, { tags: newTags }, { headers: authHeader });
      setTask((prev) => (prev ? { ...prev, tags: newTags } : prev));
      setTagsInput("");
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Add Tag ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setSavingTags(false);
    }
  };

  const removeTag = async (t: string) => {
    const existing = task?.tags || [];
    const newTags = existing.filter((x) => x !== t);
    try {
      setSavingTags(true);
      await axios.patch(`${baseUrl}/tasks/${id}`, { tags: newTags }, { headers: authHeader });
      setTask((prev) => (prev ? { ...prev, tags: newTags } : prev));
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Remove Tag ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setSavingTags(false);
    }
  };

  /* ------------------------------ reminder edit ----------------------------- */

  const openReminderDialog = () => {
    // prefill with current remindAt if present
    if (task?.remindAt) {
      const d = new Date(task.remindAt);
      const isoLocal = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setReminderDatetime(isoLocal);
    } else {
      setReminderDatetime("");
    }
    setReminderDialogOpen(true);
  };

  const saveCustomReminder = async () => {
    if (!reminderDatetime) return;
    try {
      setSavingReminder(true);
      // convert local datetime to ISO
      const local = new Date(reminderDatetime);
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { remindIn: "custom", remindAt: local.toISOString() },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, remindIn: "custom", remindAt: local.toISOString() } : prev));
      setSnackbar({ open: true, message: "Reminder saved.", severity: "success" });
      setReminderDialogOpen(false);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Save Reminder ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setSavingReminder(false);
    }
  };

  /* ------------------------------- tags edit -------------------------------- */
  const saveTags = async () => {
    try {
      setSavingTags(true);
      const tagsArray = tagsInput.split(",").map(t => t.trim()).filter(t => t.length > 0);
      await axios.patch(`${baseUrl}/tasks/${id}`, { tags: tagsArray }, { headers: authHeader });
      setTask((prev) => (prev ? { ...prev, tags: tagsArray } : prev));
      setSnackbar({ open: true, message: "Tags saved.", severity: "success" });
      setTagsInput("");
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Save Tags ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    } finally {
      setSavingTags(false);
    }
  };

  /* ------------------------------ reminder edit ----------------------------- */
  const handleReminderChange = async (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === "CUSTOM") {
      setReminderDialogOpen(true);
      return;
    }
    try {
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { remindIn: value || null, remindAt: null },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, remindIn: value || null, remindAt: null } : prev));
      setSnackbar({ open: true, message: "Reminder updated.", severity: "success" });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed To Update Reminder ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  /* ------------------------------ Enhancement Functions ----------------------------- */

  // Keyboard shortcuts handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key.toLowerCase()) {
        case 'e':
          event.preventDefault();
          navigate(`/edit-task/${id}`);
          break;
        case 'c':
          event.preventDefault();
          document.getElementById('comment-input')?.focus();
          break;
        case 's':
          event.preventDefault();
          setQuickActionAnchor(document.getElementById('quick-action-button'));
          break;
        case 'a':
          event.preventDefault();
          setAttachmentOpen(true);
          break;
        case 'escape':
          event.preventDefault();
          navigate(-1);
          break;
        case '?':
          event.preventDefault();
          setShowKeyboardShortcuts(true);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [id, navigate]);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    const mapped: FileWithTitle[] = files.map((f) => ({
      file: f,
      title: f.name,
    }));

    setNewAttachments((prev) => [...prev, ...mapped]);
    setAttachmentOpen(true);
  };

  // Copy task link
  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setSnackbar({ open: true, message: "Task link copied!", severity: "success" });
    setQuickActionAnchor(null);
  };

  // Print task
  const handlePrint = () => {
    window.print();
    setQuickActionAnchor(null);
  };

  // Quick status change
  const handleQuickStatusChange = async (newStatus: TaskStatus) => {
    try {
      await axios.patch(
        `${baseUrl}/tasks/${id}`,
        { status: newStatus },
        { headers: authHeader }
      );
      setTask((prev) => (prev ? { ...prev, status: newStatus } : prev));
      setSnackbar({ open: true, message: "Status updated!", severity: "success" });
      setQuickActionAnchor(null);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Failed to update status: ${error?.response?.data?.message || error.message}`,
        severity: "error",
      });
    }
  };

  const buildSubtaskCreationUrl = () => {
    if (!id) return null;
    const params = new URLSearchParams();
    params.set("parent", id);
    if (task?.projectId) params.set("projectId", String(task.projectId));
    if (task?.type) params.set("type", String(task.type));
    const query = params.toString();
    return `/new-task/${id}${query ? `?${query}` : ""}`;
  };

  const handleCreateSubtask = () => {
    const url = buildSubtaskCreationUrl();
    if (url) navigate(url);
  };

  const handleClearSubtaskFilters = () => {
    setStatusFilter([]);
    setPriorityFilter([]);
  };

  /* --------------------------------- render -------------------------------- */
  
  // Show skeleton loader while data is loading
  if (showSkeleton) {
  return (
    <Box
        sx={{
          minHeight: "100vh",
          background: theme.palette.mode === "dark"
            ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
            : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          pb: 4,
          px: 3,
          pt: 4,
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto" }}>
          <Stack spacing={3}>
            {/* Header Skeleton */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Skeleton variant="text" width="40%" height={40} />
                <Skeleton variant="text" width="60%" />
                <Stack direction="row" spacing={2}>
                  <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
                  <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
                </Stack>
              </Stack>
            </Paper>

            <Grid container spacing={3}>
              {/* Left Column */}
              <Grid item xs={12} lg={8}>
                <Stack spacing={3}>
                  {[1, 2, 3, 4].map((i) => (
                    <Paper key={i} elevation={0} sx={{ p: 3, borderRadius: 3 }}>
                      <Skeleton variant="text" width="30%" height={32} />
                      <Skeleton variant="rectangular" height={120} sx={{ mt: 2, borderRadius: 2 }} />
                    </Paper>
                  ))}
                </Stack>
              </Grid>

              {/* Right Column */}
              <Grid item xs={12} lg={4}>
                <Stack spacing={3}>
                  {[1, 2, 3].map((i) => (
                    <Paper key={i} elevation={0} sx={{ p: 3, borderRadius: 3 }}>
                      <Skeleton variant="text" width="50%" height={24} />
                      <Stack spacing={1.5} mt={2}>
                        <Skeleton variant="text" />
                        <Skeleton variant="text" />
                        <Skeleton variant="text" width="70%" />
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.mode === "dark"
          ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
          : "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        pb: 4,
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={handleDragLeave}
    >
      {/* Breadcrumbs Navigation */}
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: 3,
          pt: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <BreadcrumbLink onClick={() => navigate("/")}>
            <HomeIcon fontSize="small" />
            <Typography variant="body2">Home</Typography>
          </BreadcrumbLink>
          <NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />
          {task?.projectName && (
            <>
              <BreadcrumbLink onClick={() => task?.projectId && navigate(`/projects/${task.projectId}`)}>
                <FolderOpenIcon fontSize="small" />
                <Typography variant="body2">{task.projectName}</Typography>
              </BreadcrumbLink>
              <NavigateNextIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </>
          )}
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
            {task?.title}
          </Typography>
        </Stack>
      </Box>

      {/* Update Indicator */}
      {showUpdateIndicator && (
        <UpdateIndicator>
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} sx={{ color: "#fff" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Updates available - Refresh to see changes
            </Typography>
            <IconButton size="small" sx={{ color: "#fff" }} onClick={() => window.location.reload()}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </UpdateIndicator>
      )}

      {/* Drag and Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2000,
            background: alpha(theme.palette.primary.main, 0.1),
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={8}
            sx={{
              p: 8,
              borderRadius: 4,
              border: `3px dashed ${theme.palette.primary.main}`,
              background: alpha(theme.palette.background.paper, 0.95),
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CloudUploadIcon sx={{ fontSize: 64, color: theme.palette.primary.main }} />
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Drop files to upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Release to add attachments to this task
              </Typography>
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Modern Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1100,
          background: theme.palette.mode === "dark"
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha("#ffffff", 0.98),
          backdropFilter: "saturate(180%) blur(20px)",
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          boxShadow: theme.palette.mode === "dark"
            ? "0 4px 20px rgba(0, 0, 0, 0.4)"
            : "0 4px 20px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Box sx={{ maxWidth: 1400, mx: "auto", px: 3, py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            {/* Left: Back + Title */}
            <Stack direction="row" alignItems="center" spacing={2} flex={1} minWidth={0}>
              <IconButton
                onClick={() => navigate(-1)}
                sx={{
                  background: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    background: alpha(theme.palette.primary.main, 0.2),
                    transform: "translateX(-4px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {task?.title || "Loading..."}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    {task?.ref}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">•</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {task?.projectName}
                  </Typography>
                  {task?.type && (
                    <>
                      <Typography variant="body2" color="text.secondary">•</Typography>
                      <Chip 
                        label={task.type} 
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.main,
                        }}
                      />
                    </>
                  )}
                </Stack>
                {isSubtask && parentTask && (
                  <Box
                    sx={{
                      mt: 1,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      background: alpha(theme.palette.primary.main, 0.05),
                      cursor: "pointer",
                      "&:hover": {
                        background: alpha(theme.palette.primary.main, 0.1),
                      },
                    }}
                    onClick={() => navigate(`/task/${parentTask.id}`)}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                      Parent Task
                    </Typography>
                    <Avatar
                      src={parentTask.createdBy?.profile?.imageUrl || parentTask.createdBy?.imageUrl || undefined}
                      alt={parentTask.createdBy?.username || ""}
                      sx={{ width: 28, height: 28, fontSize: 13 }}
                    >
                      {(parentTask.createdBy?.username || "U").slice(0, 2).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                        {parentTask.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {parentTask.createdBy?.username || "Unknown user"}
                      </Typography>
                    </Box>
                    <ArrowForwardIosIcon sx={{ fontSize: 16, color: theme.palette.primary.main, ml: 0.5 }} />
                  </Box>
                )}
              </Box>
            </Stack>

            {/* Right: Actions */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              {/* Compact Status & Priority */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1.5,
                  background: alpha(theme.palette.background.paper, 0.8),
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                }}
              >
                {/* Status - Disabled, auto-updated based on user statuses */}
                <Tooltip title="Task status is automatically updated based on all user statuses">
                  <Select
                    size="small"
                    value={task?.status || ""}
                    disabled
                    sx={{
                      minWidth: 100,
                      height: 26,
                      "& .MuiSelect-select": {
                        py: 0.25,
                        px: 0.75,
                        fontSize: "0.7rem",
                        display: "flex",
                        alignItems: "center",
                        cursor: "not-allowed",
                      },
                      "&.Mui-disabled": {
                        opacity: 0.8,
                      },
                    }}
                    renderValue={(value) => (
                      <StatusChip
                        label={getTaskStatusLabel(value)}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          backgroundColor: getTaskStatusContainerBgColor(value),
                          color: getTaskStatusContainerTextColor(value),
                        }}
                      />
                    )}
                  >
                    {statusOptions.map((s) => (
                      <MenuItem key={s} value={s}>
                        <StatusChip
                          label={getTaskStatusLabel(s)}
                          size="small"
                          sx={{
                            backgroundColor: getTaskStatusContainerBgColor(s),
                            color: getTaskStatusContainerTextColor(s),
                          }}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ height: 20, mx: 0.5 }} />

                {/* Priority */}
                <Select
                  size="small"
                  value={task?.importance || ""}
                  onChange={handlePriorityChange}
                  sx={{
                    minWidth: 90,
                    height: 26,
                    "& .MuiSelect-select": {
                      py: 0.25,
                      px: 0.75,
                      fontSize: "0.7rem",
                    },
                  }}
                  renderValue={(value) => (
                    <PriorityBadge
                      sx={{
                        backgroundColor: alpha(getImportanceContainerColor(value), 0.15),
                        color: getImportanceContainerColor(value),
                        py: 0.15,
                        px: 0.75,
                        fontSize: "0.65rem",
                      }}
                    >
                      <FlagIcon sx={{ fontSize: 12, mr: 0.25 }} />
                      {getImportanceContainerText(value)}
                    </PriorityBadge>
                  )}
                >
                  {priorityOptions.map((imp) => (
                    <MenuItem key={imp} value={imp}>
                      <PriorityBadge
                        sx={{
                          backgroundColor: alpha(getImportanceContainerColor(imp), 0.15),
                          color: getImportanceContainerColor(imp),
                        }}
                      >
                        <FlagIcon fontSize="small" />
                        {getImportanceContainerText(imp)}
                      </PriorityBadge>
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ height: 24, mx: 0.5 }} />

              <Tooltip title={task?.public ? "Make private" : "Make public"}>
                <StyledIconButton onClick={changePrivacy} size="small">
                  {task?.public ? (
                    <PublicIcon sx={{ color: theme.palette.primary.main }} />
                  ) : (
                    <PublicOffIcon sx={{ color: theme.palette.primary.main }} />
                  )}
                </StyledIconButton>
              </Tooltip>

              <Tooltip title={`Visibility: ${task?.visibility || "—"}`}>
                <StyledIconButton size="small" onClick={(e) => setVisAnchorEl(e.currentTarget)}>
                  <VisibilityIcon sx={{ color: theme.palette.primary.main }} />
                </StyledIconButton>
              </Tooltip>

              <Tooltip title="Followers">
                <StyledIconButton
                  size="small"
                  onClick={() => {
                    setFollowersOpen(true);
                    getFollowersList();
                  }}
                >
                  <Badge badgeContent={followersCount} color="primary">
                    <GroupIcon sx={{ color: theme.palette.primary.main }} />
                  </Badge>
                </StyledIconButton>
              </Tooltip>

              <Tooltip title="Attachments">
                <StyledIconButton 
                  size="small"
                  onClick={() => setAttachmentOpen(true)}
                >
                  <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                </StyledIconButton>
              </Tooltip>

              <Tooltip title="Status History">
                <StyledIconButton onClick={() => setHistoryDialogOpen(true)} size="small">
                  <HistoryIcon sx={{ color: theme.palette.primary.main }} />
                </StyledIconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              <Tooltip title="Create Subtask">
                <span>
                  <StyledButton
                    variant="outlined"
                    startIcon={<AddTaskIcon />}
                    onClick={handleCreateSubtask}
                    disabled={!isProjectMember || projectMemberLoading}
                    sx={{
                      borderColor: theme.palette.success.main,
                      color: theme.palette.success.main,
                      "&:hover": {
                        borderColor: theme.palette.success.dark,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                      },
                    }}
                  >
                    Add Subtask
                  </StyledButton>
                </span>
              </Tooltip>

              <StyledButton
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/edit-task/${id}`)}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  },
                }}
              >
                Edit Task
              </StyledButton>

              <Tooltip title="Delete Task">
                <StyledButton
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    if (task?.parentTaskId === null && (subTasks?.length || 0) > 0) {
                      setSnackbar({
                        open: true,
                        message: "This task has sub-tasks. Delete them first.",
                        severity: "warning",
                      });
                      return;
                    }
                    setDeleteTaskOpen(true);
                  }}
                  sx={{
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    "&:hover": {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  Delete
                </StyledButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      </Box>

      {/* Main Content Area with Modern Grid */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: 3, py: 4 }}>
        <Grid container spacing={3}>
          {/* Left Column - Main Content */}
          <Grid item xs={12} lg={8}>
            <Stack spacing={3}>

              {/* User Statuses Section - Display only current user's status */}
              {assignmentsByStep && assignmentsByStep.length > 0 && (() => {
                // Filter to show only current user, preserving step info
                const currentUserAssignments = assignmentsByStep.flatMap((stepData: any) =>
                  (stepData.assignees?.filter((assignee: any) => assignee?.id === userId) || []).map((assignee: any) => ({
                    ...assignee,
                    stepOrder: stepData.stepOrder,
                    stepId: stepData.stepId,
                  }))
                );
                
                return currentUserAssignments.length > 0 ? (
                  <Fade in timeout={600}>
                    <Box
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: alpha(theme.palette.background.paper, 0.6),
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <PersonOutlineIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          My Status
                        </Typography>
                      </Box>
                      <Stack spacing={1.5}>
                        {currentUserAssignments.map((assignee: any, idx: number) => {
                          const userStatus = assignee?.userTaskStatus || "DONE";
                          const userStatusDisplay = getUserTaskStatusDisplay(userStatus);
                          const userImage = assignee?.imageUrl || "";
                          const userName = assignee?.username || "Unknown User";
                          const userDescription = assignee?.userDescription;
                          const stepOrder = assignee?.stepOrder || 0;
                          const uniqueKey = `current-user-${idx}`;
                          
                          return (
                            <Box
                              key={uniqueKey}
                              sx={{
                                borderRadius: 1.5,
                                background: alpha(theme.palette.background.paper, 0.4),
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                transition: "all 0.2s ease",
                                overflow: "hidden",
                                "&:hover": {
                                  background: alpha(theme.palette.background.paper, 0.6),
                                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  p: 1.5,
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1 }}>
                                  <Avatar
                                    src={userImage}
                                    alt={userName}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      fontSize: 14,
                                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    }}
                                  >
                                    {!userImage && userName.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 600,
                                        fontSize: "0.875rem",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {userName}
                                    </Typography>
                                    {stepOrder > 0 && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          fontSize: "0.7rem",
                                        }}
                                      >
                                        Step {stepOrder}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                  {userStatusDisplay.icon}
                                  <Select
                                    value={userStatus}
                                    onChange={(e) => {
                                      if (id && assignee?.id) {
                                        handleUserTaskStatusChange(Number(id), assignee.id, e.target.value);
                                      }
                                    }}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      minWidth: 120,
                                      height: 28,
                                      borderRadius: 1.5,
                                      "& .MuiSelect-select": {
                                        py: 0.5,
                                        px: 1,
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        backgroundColor: alpha(userStatusDisplay.color, 0.15),
                                        color: userStatusDisplay.color,
                                        border: `1px solid ${alpha(userStatusDisplay.color, 0.3)}`,
                                        "&:hover": {
                                          backgroundColor: alpha(userStatusDisplay.color, 0.25),
                                        },
                                      },
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        border: `1px solid ${alpha(userStatusDisplay.color, 0.3)}`,
                                      },
                                      "&:hover .MuiOutlinedInput-notchedOutline": {
                                        border: `1px solid ${alpha(userStatusDisplay.color, 0.5)}`,
                                      },
                                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                        border: `1px solid ${userStatusDisplay.color}`,
                                      },
                                    }}
                                    renderValue={(value) => {
                                      const statusDisplay = getUserTaskStatusDisplay(value);
                                      return statusDisplay.label;
                                    }}
                                  >
                                    {userTaskStatusOptions.map((option) => {
                                      const optionDisplay = getUserTaskStatusDisplay(option.value);
                                      return (
                                        <MenuItem key={option.value} value={option.value}>
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {optionDisplay.icon}
                                            <Typography
                                              sx={{
                                                color: optionDisplay.color,
                                                fontWeight: 600,
                                                fontSize: "0.75rem",
                                              }}
                                            >
                                              {option.label}
                                            </Typography>
                                          </Box>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                </Box>
                              </Box>
                              
                              {/* User Description Section */}
                              {userDescription && (
                                <Box>
                                  <Box
                                    onClick={() => setExpandedDescriptions((prev) => ({ ...prev, [uniqueKey]: !prev[uniqueKey] }))}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      px: 1.5,
                                      py: 0.75,
                                      cursor: "pointer",
                                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                      background: alpha(theme.palette.primary.main, 0.05),
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        background: alpha(theme.palette.primary.main, 0.1),
                                      },
                                    }}
                                  >
                                    <DescriptionIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        flex: 1,
                                        fontWeight: 600,
                                        color: theme.palette.primary.main,
                                        fontSize: "0.7rem",
                                      }}
                                    >
                                      User Description
                                    </Typography>
                                    {expandedDescriptions[uniqueKey] !== false ? (
                                      <ExpandLessIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                    ) : (
                                      <ExpandMoreIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                    )}
                                  </Box>
                                  <Collapse in={expandedDescriptions[uniqueKey] !== false}>
                                    <Box
                                      sx={{
                                        px: 1.5,
                                        py: 1.5,
                                        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                        background: alpha(theme.palette.background.paper, 0.6),
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: "0.8rem",
                                          lineHeight: 1.6,
                                          color: "text.primary",
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {userDescription}
                                      </Typography>
                                    </Box>
                                  </Collapse>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  </Fade>
                ) : null;
              })()}


              {/* Progress - Super Minimal Single Line */}
              <Fade in timeout={800}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.secondary.main, 0.08)})`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}
                >
                  <Chip
                    label={`${progressDraft}%`}
                    size="small"
                    sx={{
                      height: 26,
                      fontWeight: 800,
                      fontSize: "0.75rem",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                      color: "#fff",
                      minWidth: 50,
                    }}
                  />
                  <Box flex={1}>
                    <Slider
                      value={progressDraft}
                      onChange={(_, val) => setProgressDraft(val as number)}
                      onChangeCommitted={(_, val) => saveProgress(val as number)}
                      disabled={progressSaving}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100}
                      size="small"
                      sx={{
                        height: 6,
                        "& .MuiSlider-thumb": {
                          width: 12,
                          height: 12,
                        },
                        "& .MuiSlider-track": {
                          height: 6,
                          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        },
                        "& .MuiSlider-rail": {
                          height: 6,
                          opacity: 0.3,
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.7rem", minWidth: 60 }}>
                    Progress
                  </Typography>
                </Box>
              </Fade>

              {/* Description Card */}
              <Fade in timeout={1000}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <SectionHeader>
                      <DescriptionIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Description
                      </Typography>
                    </SectionHeader>
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.8,
                        color: "text.primary",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {task?.description || "No description provided."}
                    </Typography>
                  </CardContent>
                </GlassCard>
              </Fade>

              {/* Subtasks - Enhanced controls */}
              {task?.parentTaskId == null && (
                <Fade in timeout={1200}>
                  <GlassCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 2.5, flexWrap: "wrap" }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <FormatListNumberedIcon sx={{ color: theme.palette.primary.main }} />
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Subtasks ({totalSubtasks})
                          </Typography>
                          {subtaskFiltersApplied && (
                            <Chip
                              size="small"
                              color="primary"
                              label={`Showing ${sortedSubtasks.length} of ${totalSubtasks}`}
                              sx={{ fontWeight: 700 }}
                            />
                          )}
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Tooltip title="Create Subtask">
                            <span>
                              <StyledIconButton
                                size="small"
                                onClick={handleCreateSubtask}
                                disabled={!isProjectMember || projectMemberLoading}
                                sx={{
                                  background: alpha(theme.palette.success.main, 0.1),
                                  "&:hover": {
                                    background: alpha(theme.palette.success.main, 0.2),
                                  },
                                }}
                              >
                                <AddTaskIcon
                                  sx={{ color: isProjectMember ? theme.palette.success.main : theme.palette.text.disabled }}
                                />
                              </StyledIconButton>
                            </span>
                          </Tooltip>
                          <ToggleButtonGroup
                            value={subTaskView}
                            exclusive
                            onChange={(_, val) => val && setSubTaskView(val)}
                            size="small"
                          >
                            <ToggleButton value="grid">
                              <ViewModuleOutlinedIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="list">
                              <ViewListOutlinedIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="board">
                              <ViewKanbanOutlinedIcon fontSize="small" />
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      </Box>

                      {hasSubtasks && (
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mb: subtaskFiltersApplied ? 2 : 3,
                          }}
                        >
                          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
                            <InputLabel id="subtask-sort-label">Sort by</InputLabel>
                            <Select
                              labelId="subtask-sort-label"
                              label="Sort by"
                              value={subTaskSort}
                              onChange={(e) => setSubTaskSort(e.target.value as SubTaskSort)}
                            >
                              <MenuItem value="createdDesc">Created ↓</MenuItem>
                              <MenuItem value="createdAsc">Created ↑</MenuItem>
                              <MenuItem value="updatedDesc">Updated ↓</MenuItem>
                              <MenuItem value="updatedAsc">Updated ↑</MenuItem>
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
                            <InputLabel id="subtask-status-label">Status</InputLabel>
                            <Select
                              labelId="subtask-status-label"
                              label="Status"
                              multiple
                              value={statusFilter}
                              onChange={(e) =>
                                setStatusFilter(
                                  typeof e.target.value === "string"
                                    ? (e.target.value.split(",") as TaskStatus[])
                                    : (e.target.value as TaskStatus[])
                                )
                              }
                              renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {selected.map((s) => (
                                    <Chip
                                      key={s}
                                      size="small"
                                      label={getTaskStatusLabel(s)}
                                      sx={{
                                        backgroundColor: getTaskStatusContainerBgColor(s),
                                        color: getTaskStatusContainerTextColor(s),
                                        fontWeight: 700,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            >
                              {statusOptions.map((s) => (
                                <MenuItem key={s} value={s}>
                                  <Checkbox checked={statusFilter.indexOf(s) > -1} />
                                  <ListItemText primary={getTaskStatusLabel(s)} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 190 }, flex: { xs: "1 1 100%", sm: "0 0 auto" } }}>
                            <InputLabel id="subtask-priority-label">Priority</InputLabel>
                            <Select
                              labelId="subtask-priority-label"
                              label="Priority"
                              multiple
                              value={priorityFilter}
                              onChange={(e) =>
                                setPriorityFilter(
                                  typeof e.target.value === "string"
                                    ? (e.target.value.split(",") as TaskImportance[])
                                    : (e.target.value as TaskImportance[])
                                )
                              }
                              renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {selected.map((p) => (
                                    <Chip
                                      key={p}
                                      size="small"
                                      label={getImportanceContainerText(p)}
                                      sx={{
                                        backgroundColor: getImportanceContainerColor(p),
                                        color: "#fff",
                                        fontWeight: 700,
                                      }}
                                    />
                                  ))}
                                </Box>
                              )}
                            >
                              {priorityOptions.map((p) => (
                                <MenuItem key={p} value={p}>
                                  <Checkbox checked={priorityFilter.indexOf(p) > -1} />
                                  <ListItemText primary={getImportanceContainerText(p)} />
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                          {subtaskFiltersApplied && (
                            <Button size="small" onClick={handleClearSubtaskFilters} sx={{ fontWeight: 600 }}>
                              Clear filters
                            </Button>
                          )}
                        </Box>
                      )}

                      {hasSubtasks ? (
                        subTaskView === "board" ? (
                          <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", pb: 0.5 }}>
                            {statusOptions.map((col) => {
                              const items = (groupedSubtasksByStatus[col] || []) as SubtaskExtra[];
                              return (
                                <Paper
                                  key={col}
                                  variant="outlined"
                                  sx={{
                                    minWidth: 280,
                                    maxWidth: 340,
                                    borderRadius: 2,
                                    p: 1.5,
                                    background: alpha(theme.palette.background.paper, 0.7),
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={800}>
                                      {getTaskStatusLabel(col)}
                                    </Typography>
                                    <Chip size="small" label={items.length} />
                                  </Box>
                                  <Stack spacing={1.25}>
                                    {items.length ? (
                                      items.map((t) => (
                                        <Card
                                          key={t.id}
                                          onClick={() => navigate(`/task/${t.id}`)}
                                          sx={{
                                            borderRadius: 2,
                                            cursor: "pointer",
                                            p: 1.5,
                                            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                            "&:hover": { boxShadow: theme.shadows[4] },
                                          }}
                                        >
                                          <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                                            <Avatar
                                              src={t.createdBy?.profile?.imageUrl || ""}
                                              alt={t.createdBy?.username || ""}
                                              sx={{ width: 24, height: 24 }}
                                            />
                                            {t.importance && (
                                              <Box
                                                sx={{
                                                  width: 8,
                                                  height: 8,
                                                  borderRadius: "50%",
                                                  bgcolor: getImportanceContainerColor(t.importance),
                                                }}
                                              />
                                            )}
                                            <Typography
                                              sx={{
                                                fontSize: 14,
                                                fontWeight: 700,
                                                color: theme.palette.primary.main,
                                                ...leftAlignedSingleLine(t.title),
                                              }}
                                              title={t.title}
                                            >
                                              {t.title}
                                            </Typography>
                                          </Box>
                                          {t.description && (
                                            <Typography
                                              variant="body2"
                                              color="text.secondary"
                                              sx={{ ...leftAlignedClamp(2, t.description) }}
                                            >
                                              {t.description}
                                            </Typography>
                                          )}
                                          <Box
                                            mt={0.75}
      display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                          >
                                            <Chip
                                              size="small"
                                              label={t.project?.name || "No project"}
                                              sx={{ height: 22 }}
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                              {t.endDate
                                                ? handleDate(t.endDate)
                                                : t.startDate
                                                ? handleDate(t.startDate)
                                                : "—"}
                                            </Typography>
                                          </Box>
                                        </Card>
                                      ))
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        No tasks in this column.
                                      </Typography>
                                    )}
                                  </Stack>
                                </Paper>
                              );
                            })}
                          </Box>
                        ) : hasFilteredSubtasks ? (
                          subTaskView === "grid" ? (
                            <Grid container spacing={2}>
                              {sortedSubtasks.map((subtask) => (
                                <Grid item xs={12} sm={6} md={4} key={subtask.id}>
                                  <Card
                                    sx={{
                                      borderRadius: 2,
                                      cursor: "pointer",
                                      transition: "all 0.3s ease",
                                      border: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
                                      "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: 6,
                                        borderColor: theme.palette.primary.main,
                                      },
                                    }}
                                    onClick={() => navigate(`/task/${subtask.id}`)}
                                  >
                                    <CardContent sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                          <Avatar
                                            src={
                                              subtask.createdBy?.profile?.imageUrl ||
                                              (subtask.createdBy as any)?.imageUrl ||
                                              undefined
                                            }
                                            alt={subtask.createdBy?.username || "Creator"}
                                            sx={{ width: 32, height: 32, fontSize: 14 }}
                                          >
                                            {(subtask.createdBy?.username || "U").slice(0, 2).toUpperCase()}
                                          </Avatar>
                                          <Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                              Created by
                                            </Typography>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                              {subtask.createdBy?.username || "Unknown user"}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <StatusChip
                                          label={getTaskStatusLabel(subtask.status)}
                                          size="small"
                                          sx={{
                                            backgroundColor: getTaskStatusContainerBgColor(subtask.status),
                                            color: getTaskStatusContainerTextColor(subtask.status),
                                          }}
                                        />
                                      </Box>

                                      <Typography
                                        variant="body1"
                                        sx={{
                                          fontWeight: 700,
                                          mb: 0.5,
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          display: "-webkit-box",
                                          WebkitLineClamp: 2,
                                          WebkitBoxOrient: "vertical",
                                        }}
                                      >
                                        {subtask.title}
                                      </Typography>
                                      {subtask.importance && (
                                        <Chip
                                          label={getImportanceContainerText(subtask.importance)}
                                          size="small"
                                          sx={{
                                            alignSelf: "flex-start",
                                            backgroundColor: alpha(getImportanceContainerColor(subtask.importance), 0.15),
                                            color: getImportanceContainerColor(subtask.importance),
                                            fontWeight: 600,
                                          }}
                                        />
                                      )}
                                    </CardContent>
                                  </Card>
                                </Grid>
                              ))}
                            </Grid>
                          ) : (
                            <Stack spacing={1.5}>
                              {sortedSubtasks.map((subtask) => (
                                <Card
                                  key={subtask.id}
                                  sx={{
                                    borderRadius: 2,
                                    cursor: "pointer",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                      transform: "translateX(8px)",
                                      boxShadow: 4,
                                    },
                                  }}
                              onClick={() => navigate(`/task/${subtask.id}`)}
                                >
                                  <CardContent sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
                                <Avatar
                                  src={
                                    subtask.createdBy?.profile?.imageUrl ||
                                    (subtask.createdBy as any)?.imageUrl ||
                                    undefined
                                  }
                                  alt={subtask.createdBy?.username || "Creator"}
                                  sx={{ width: 32, height: 32, fontSize: 14 }}
                                >
                                  {(subtask.createdBy?.username || "U").slice(0, 2).toUpperCase()}
                                </Avatar>
                                <Box sx={{ minWidth: 120 }}>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Created by
                                  </Typography>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                                    {subtask.createdBy?.username || "Unknown user"}
                                  </Typography>
                                </Box>
                                <StatusChip
                                  label={getTaskStatusLabel(subtask.status)}
                                  size="small"
                                  sx={{
                                    backgroundColor: getTaskStatusContainerBgColor(subtask.status),
                                    color: getTaskStatusContainerTextColor(subtask.status),
                                  }}
                                />
                                <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, minWidth: 160 }}>
                                  {subtask.title}
                                </Typography>
                                {subtask.importance && (
                                  <Chip
                                    label={getImportanceContainerText(subtask.importance)}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(getImportanceContainerColor(subtask.importance), 0.15),
                                      color: getImportanceContainerColor(subtask.importance),
                                      fontWeight: 600,
                                    }}
                                  />
                                )}
                                  </CardContent>
                                </Card>
                              ))}
                            </Stack>
                          )
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 4,
                              textAlign: "center",
                              background: alpha(theme.palette.warning.main, 0.05),
                              borderRadius: 3,
                              border: `2px dashed ${alpha(theme.palette.warning.main, 0.2)}`,
                            }}
                          >
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              No subtasks match the current filters
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Adjust or clear your filters to see subtasks again.
                            </Typography>
                            <Button size="small" sx={{ mt: 2 }} onClick={handleClearSubtaskFilters}>
                              Clear filters
                            </Button>
                          </Paper>
                        )
                      ) : (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 4,
                            textAlign: "center",
                            background: alpha(theme.palette.info.main, 0.05),
                            borderRadius: 3,
                            border: `2px dashed ${alpha(theme.palette.info.main, 0.2)}`,
                          }}
                        >
                          <FormatListNumberedIcon
                            sx={{ fontSize: 48, color: alpha(theme.palette.info.main, 0.5), mb: 2 }}
                          />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            No Subtasks Yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Break down this task into smaller steps
                          </Typography>
                          <StyledButton
                            variant="contained"
                            size="small"
                            startIcon={<AddTaskIcon />}
                            onClick={handleCreateSubtask}
                            disabled={!isProjectMember || projectMemberLoading}
                            sx={{ mt: 2 }}
                          >
                            Create Subtask
                          </StyledButton>
                        </Paper>
                      )}
                    </CardContent>
                  </GlassCard>
                </Fade>
              )}

              {/* Parent Task Preview */}
              {parentTask && (
                <Fade in timeout={1200}>
                  <GlassCard>
                  <CardActionArea onClick={() => navigate(`/task/${parentTask.id}`)}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase" }}>
                          Parent Task
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
                          {parentTask.title}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                          <Avatar
                            src={parentTask.createdBy?.profile?.imageUrl || parentTask.createdBy?.imageUrl}
                            sx={{ width: 24, height: 24 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {parentTask.createdBy?.username}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </GlassCard>
                </Fade>
              )}

              {/* Approvals Section */}
              {approvalsData?.policy && (
                <Fade in timeout={1400}>
                  <GlassCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                        {approvalOverallLabel && (
                          <>
                            {approvalOverallLabel.icon}
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Approval: {approvalOverallLabel.label}
                            </Typography>
                          </>
                        )}
                      </Box>
                      <Stack spacing={2}>
                        {approvalsByStep.map(({ step, items }) => (
                          <Paper
                            key={step.id}
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                              background: alpha(theme.palette.background.paper, 0.6),
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                              Step {step.stepOrder} ({step.requiredStrategy})
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {items.map((ap) => (
                                <Chip
                                  key={ap.id}
                                  avatar={
                                    <Avatar
                                      src={ap.approver?.profile?.imageUrl || ""}
                                      alt={ap.approver?.username || ""}
                                      sx={{ width: 24, height: 24 }}
                                    />
                                  }
                                  label={`${ap.approver?.username || "—"} • ${ap.status}`}
                                  size="small"
                                  sx={{
                                    fontWeight: 600,
                                  }}
                                />
                              ))}
                            </Box>
                          </Paper>
                        ))}
                      </Stack>
                    </CardContent>
                  </GlassCard>
                </Fade>
              )}

              {/* Assignment Steps */}
              {assignmentsByStep && assignmentsByStep.length > 0 && (
                <Fade in timeout={1600}>
                  <GlassCard>
                    <CardContent sx={{ p: 3 }}>
                      <SectionHeader>
                        <AssignmentIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Assignment Steps
                        </Typography>
                      </SectionHeader>
                      <Stack spacing={2}>
                        {assignmentsByStep.map((stepData, idx) => (
                          <Paper
                            key={idx}
                            elevation={0}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                              background: alpha(theme.palette.primary.main, 0.05),
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                              Step {stepData.stepOrder} - {stepData.assignmentType}
                            </Typography>
                            <AvatarGroup max={5}>
                              {stepData.assignees?.map((assignee: any, i: number) => (
                                <Avatar
                                  key={i}
                                  src={assignee.imageUrl || ""}
                                  alt={assignee.username}
                                  sx={{ width: 32, height: 32 }}
                                />
                              ))}
                            </AvatarGroup>
                          </Paper>
                        ))}
                      </Stack>
                    </CardContent>
                  </GlassCard>
                </Fade>
              )}

              {/* Assigned Team - Using assignmentsByStep for correct user data */}
              {assignmentsByStep && assignmentsByStep.length > 0 && (
                <Fade in timeout={1800}>
                  <GlassCard>
                    <CardContent sx={{ p: 3 }}>
                      <SectionHeader>
                        <PeopleIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Assigned Team
                        </Typography>
                      </SectionHeader>
                      <Stack spacing={2}>
                        {assignmentsByStep.map((stepData: any, stepIdx: number) => (
                          <Box key={stepIdx}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 1, display: "block" }}>
                              Step {stepData.stepOrder} - {stepData.assignmentType}
                            </Typography>
                            <Stack spacing={1.5}>
                              {stepData.assignees?.map((assignee: any, idx: number) => {
                                const userImage = assignee?.imageUrl || "";
                                const userName = assignee?.username || "Unknown User";
                                const userStatus = assignee?.userTaskStatus || "ASSIGNED";
                                
                                return (
                                  <Paper
                                    key={`${stepIdx}-${idx}`}
                                    elevation={0}
                                    sx={{
                                      p: 2,
                                      borderRadius: 2,
                                      border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                      background: alpha(theme.palette.primary.main, 0.05),
                                      transition: "all 0.2s ease",
                                      "&:hover": {
                                        background: alpha(theme.palette.primary.main, 0.1),
                                        transform: "translateY(-2px)",
                                        boxShadow: 2,
                                      },
                                    }}
                                  >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                      <Avatar
                                        src={userImage}
                                        alt={userName}
                                        sx={{ 
                                          width: 48, 
                                          height: 48,
                                          border: `2px solid ${theme.palette.primary.main}`,
                                        }}
                                      >
                                        {!userImage && userName.charAt(0).toUpperCase()}
                                      </Avatar>
                                      <Box flex={1}>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                          {userName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          ID: {assignee?.id || "—"}
                                        </Typography>
                                        
                                        {/* User Description */}
                                        {assignee?.userDescription && (
                                          <Box
                                            sx={{
                                              mt: 1.5,
                                              p: 1.5,
                                              borderRadius: 1.5,
                                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                              transition: "all 0.2s ease",
                                              "&:hover": {
                                                backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontWeight: 700,
                                                color: theme.palette.primary.main,
                                                fontSize: "0.7rem",
                                                mb: 0.75,
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                              }}
                                            >
                                              <DescriptionIcon sx={{ fontSize: 14 }} />
                                              User Description
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.8rem",
                                                color: "text.primary",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {assignee.userDescription}
                                            </Typography>
                                          </Box>
                                        )}
                                        
                                        {/* Comment */}
                                        {assignee?.comment && (
                                          <Box
                                            sx={{
                                              mt: assignee?.userDescription ? 1 : 1.5,
                                              p: 1.5,
                                              borderRadius: 1.5,
                                              backgroundColor: alpha(theme.palette.info.main, 0.1),
                                              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                              transition: "all 0.2s ease",
                                              "&:hover": {
                                                backgroundColor: alpha(theme.palette.info.main, 0.15),
                                                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                              },
                                            }}
                                          >
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                                fontWeight: 700,
                                                color: theme.palette.info.main,
                                                fontSize: "0.7rem",
                                                mb: 0.75,
                                                textTransform: "uppercase",
                                                letterSpacing: 0.5,
                                              }}
                                            >
                                              <DescriptionIcon sx={{ fontSize: 14 }} />
                                              Status Comment
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.8rem",
                                                color: "text.primary",
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                                lineHeight: 1.6,
                                              }}
                                            >
                                              {assignee.comment}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                      <Chip 
                                        label={userStatus === "DONE" ? "Done" : userStatus === "IN_PROGRESS" ? "In Progress" : "To Do"}
                                        size="small"
                                        sx={{ 
                                          fontWeight: 600,
                                          bgcolor: userStatus === "DONE" 
                                            ? alpha(theme.palette.success.main, 0.15)
                                            : userStatus === "IN_PROGRESS"
                                            ? alpha(theme.palette.warning.main, 0.15)
                                            : alpha(theme.palette.grey[500], 0.15),
                                          color: userStatus === "DONE" 
                                            ? theme.palette.success.main
                                            : userStatus === "IN_PROGRESS"
                                            ? theme.palette.warning.main
                                            : theme.palette.grey[700],
                                        }}
                                      />
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </GlassCard>
                </Fade>
              )}

              {/* Advanced Fields */}
              <Fade in timeout={2000}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <SectionHeader>
                      <AccessTimeIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Time Tracking & Points
                      </Typography>
                    </SectionHeader>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Estimate
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={estimateMinutesDraft}
                          onChange={(e) => setEstimateMinutesDraft(e.target.value === "" ? "" : Number(e.target.value))}
                          placeholder="Minutes"
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Spent
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={spentMinutesDraft}
                          onChange={(e) => setSpentMinutesDraft(e.target.value === "" ? "" : Number(e.target.value))}
                          placeholder="Minutes"
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                          Points
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={pointsDraft}
                          onChange={(e) => setPointsDraft(e.target.value === "" ? "" : Number(e.target.value))}
                          placeholder="Story points"
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                    </Grid>
                    <StyledButton
                      variant="contained"
                      onClick={saveAdvancedFields}
                      disabled={savingAdvanced}
                      fullWidth
                      sx={{ mt: 2 }}
                    >
                      {savingAdvanced ? <CircularProgress size={20} /> : "Save Advanced Fields"}
                    </StyledButton>
                  </CardContent>
                </GlassCard>
              </Fade>

              {/* Tags Section - Editable */}
              <Fade in timeout={2200}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <SectionHeader>
                      <LabelIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Tags
                      </Typography>
                    </SectionHeader>
                    {task?.tags && task.tags.length > 0 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                        {task.tags.map((tag, idx) => (
                          <Chip
                            key={idx}
                            label={tag}
                            size="small"
                            sx={{
                              borderRadius: 2,
                              fontWeight: 600,
                              background: `linear-gradient(135deg, ${alpha(
                                theme.palette.primary.main,
                                0.2
                              )}, ${alpha(theme.palette.secondary.main, 0.2)})`,
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add tags (comma separated)"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <StyledButton
                      variant="contained"
                      onClick={saveTags}
                      disabled={savingTags}
                      fullWidth
                      sx={{ mt: 1.5 }}
                    >
                      {savingTags ? <CircularProgress size={20} /> : "Save Tags"}
                    </StyledButton>
                  </CardContent>
                </GlassCard>
              </Fade>

            </Stack>
          </Grid>

          {/* Right Column - Sidebar */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              
              {/* Details Card */}
              <Fade in timeout={600}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Details
                    </Typography>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Created By
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                          <Avatar
                            src={task?.createdBy?.profile?.imageUrl || task?.createdBy?.imageUrl}
                            alt={task?.createdBy?.username}
                            sx={{ width: 32, height: 32 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {task?.createdBy?.username}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Assigned Team Shortcut */}
                      {assignmentsByStep && assignmentsByStep.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Assigned Team
                          </Typography>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                            <AvatarGroup 
                              max={4} 
                              sx={{
                                "& .MuiAvatar-root": {
                                  width: 32,
                                  height: 32,
                                  fontSize: 14,
                                  border: `2px solid ${theme.palette.background.paper}`,
                                },
                              }}
                            >
                              {assignmentsByStep.flatMap((stepData: any) =>
                                (stepData.assignees || []).map((assignee: any, idx: number) => {
                                  const userImage = assignee?.imageUrl || "";
                                  const userName = assignee?.username || "Unknown";
                                  return (
                                    <Tooltip key={`${stepData.stepOrder}-${idx}`} title={userName} arrow>
                                      <Avatar
                                        src={userImage}
                                        alt={userName}
                                        sx={{ 
                                          width: 32, 
                                          height: 32,
                                          cursor: "pointer",
                                          transition: "all 0.2s ease",
                                          "&:hover": {
                                            transform: "scale(1.15)",
                                            zIndex: 10,
                                          },
                                        }}
                                      >
                                        {!userImage && userName.charAt(0).toUpperCase()}
                                      </Avatar>
                                    </Tooltip>
                                  );
                                })
                              )}
                            </AvatarGroup>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                fontWeight: 600,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                background: alpha(theme.palette.primary.main, 0.1),
                              }}
                            >
                              {assignmentsByStep.reduce((total: number, step: any) => 
                                total + (step.assignees?.length || 0), 0
                              )} member{assignmentsByStep.reduce((total: number, step: any) => 
                                total + (step.assignees?.length || 0), 0
                              ) !== 1 ? 's' : ''}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Project
                        </Typography>
                        <Box 
                          sx={{ 
                            display: "flex", 
                            alignItems: "center", 
                            gap: 1, 
                            mt: 0.5,
                            cursor: task?.projectId ? "pointer" : "default",
                            "&:hover": task?.projectId ? {
                              color: theme.palette.primary.main,
                            } : {},
                            transition: "all 0.2s ease",
                          }}
                          onClick={() => task?.projectId && navigate(`/projects/${task.projectId}`)}
                        >
                          <PieChartIcon fontSize="small" color="action" />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {task?.projectName || "—"}
                          </Typography>
                        </Box>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Start Date
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                          <CalendarTodayIcon fontSize="small" color="action" />
                          <Typography variant="body2">{task?.startDate ? handleDate(task.startDate) : "—"}</Typography>
                        </Box>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Due Date
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="body2">{task?.endDate ? handleDate(task.endDate) : "—"}</Typography>
                        </Box>
                      </Box>
                      {typeof task?.estimateMinutes === "number" && (
                        <>
                          <Divider />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Estimated Time
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {Math.floor(task.estimateMinutes / 60)}h {task.estimateMinutes % 60}m
                            </Typography>
                          </Box>
                        </>
                      )}
                      {typeof task?.spentMinutes === "number" && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Time Spent
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {Math.floor(task.spentMinutes / 60)}h {task.spentMinutes % 60}m
                          </Typography>
                        </Box>
                      )}
                      <Divider />
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Followers
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {followersCount} {followersCount === 1 ? "follower" : "followers"}
                        </Typography>
                      </Box>
                      {(task?.accountId || task?.accountName) && (
                        <>
                          <Divider />
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>
                              Linked Account
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                mt: 1,
                                p: 2,
                                borderRadius: 2,
                                background: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                cursor: task?.accountId ? "pointer" : "default",
                                transition: "all 0.2s ease",
                                "&:hover": task?.accountId ? {
                                  background: alpha(theme.palette.primary.main, 0.1),
                                  transform: "translateX(4px)",
                                } : {},
                              }}
                              onClick={() => task?.accountId && navigate(`/accounts/${task.accountId}`)}
                            >
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: task?.accountEmail || task?.accountDomain || task?.accountSlug ? 1 : 0 }}>
                                <BusinessIcon sx={{ color: theme.palette.primary.main }} />
                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                  {task.accountName || `Account #${task.accountId}`}
                                </Typography>
                              </Box>
                              {task?.accountEmail && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  📧 {task.accountEmail}
                                </Typography>
                              )}
                              {task?.accountDomain && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  🌐 {task.accountDomain}
                                </Typography>
                              )}
                              {task?.accountSlug && (
                                <Chip 
                                  label={task.accountSlug} 
                                  size="small" 
                                  sx={{ 
                                    mt: 1, 
                                    height: 20,
                                    fontSize: "0.7rem",
                                    fontWeight: 600,
                                  }} 
                                />
                              )}
                            </Paper>
                          </Box>
                        </>
                      )}
                      {task?.archived && (
                        <>
                          <Divider />
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                              Archived
                            </Typography>
                            <Switch
                              checked={!!task.archived}
                              onChange={(_, ch) => handleArchivedToggle(ch)}
                              disabled={archivedSaving}
                              size="small"
                            />
                          </Box>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </GlassCard>
              </Fade>

              {/* Comments Card */}
              <Fade in timeout={1000}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                      Comments ({comments.length})
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 400,
                        overflowY: "auto",
                        mb: 2,
                        "&::-webkit-scrollbar": { width: 6 },
                        "&::-webkit-scrollbar-thumb": {
                          background: alpha(theme.palette.primary.main, 0.3),
                          borderRadius: 3,
                        },
                      }}
                    >
                      {fetchingComments ? (
                        <Stack spacing={2}>
                          {[1, 2, 3].map((k) => (
                            <Box key={k} sx={{ display: "flex", gap: 1.5 }}>
                              <Skeleton variant="circular" width={36} height={36} />
                              <Box sx={{ flex: 1 }}>
                                <Skeleton variant="text" width="60%" />
                                <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 1, mt: 1 }} />
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      ) : comments.length === 0 ? (
                        <Paper
                          elevation={0}
                          sx={{
                            p: 4,
                            textAlign: "center",
                            background: alpha(theme.palette.secondary.main, 0.05),
                            borderRadius: 3,
                            border: `2px dashed ${alpha(theme.palette.secondary.main, 0.2)}`,
                          }}
                        >
                          <SendIcon sx={{ fontSize: 48, color: alpha(theme.palette.secondary.main, 0.5), mb: 2 }} />
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            No Comments Yet
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Be the first to share your thoughts!
                          </Typography>
                        </Paper>
                      ) : (
                        <Stack spacing={2}>
                          {comments.map((comment) => (
                            <Card
                              key={comment.id}
                              sx={{
                                borderRadius: 2,
                                background: alpha(theme.palette.background.paper, 0.6),
                              }}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                                  <Avatar
                                    src={comment.user.profile?.imageUrl || comment.user.imageUrl}
                                    alt={comment.user.username}
                                    sx={{ width: 36, height: 36 }}
                                  />
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {comment.user.username}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {handleDateWithTime(comment.date)}
                                    </Typography>
                                  </Box>
                                  {(comment.user.id === userId || role === "admin") && (
                                    <Tooltip title="Delete comment">
                                      <IconButton size="small" color="error" onClick={() => handleCommentDeleteClick(comment.id)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  {comment.text}
                                </Typography>
                                {comment.imageUrl && (
                                  <Box
                                    component="img"
                                    src={comment.imageUrl}
                                    alt="Comment attachment"
                                    sx={{
                                      width: "100%",
                                      maxHeight: 200,
                                      objectFit: "cover",
                                      borderRadius: 2,
                                      mt: 1.5,
                                      cursor: "pointer",
                                    }}
                                    onClick={() => setOpenImageCommentId(comment.id)}
                                  />
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </Stack>
                      )}
                    </Box>

                    {/* Comment Input */}
                    <Box>
                      <TextField
                        id="comment-input"
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Write a comment..."
                        value={newCommentText}
                        onChange={(e) => setNewCommentText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleCommentSubmit();
                          }
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            background: alpha(theme.palette.background.paper, 0.6),
                          },
                        }}
                      />
                      {newCommentImageSrc && (
                        <Box sx={{ mt: 1, position: "relative", display: "inline-block" }}>
                          <img
                            src={newCommentImageSrc}
                            alt="Preview"
                            style={{
                              width: 80,
                              height: 80,
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: -8,
                              right: -8,
                              background: theme.palette.error.main,
                              color: "#fff",
                              "&:hover": { background: theme.palette.error.dark },
                            }}
                            onClick={handleImageCancel}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
                        <IconButton component="label" color="primary">
                          <InsertPhotoIcon />
                          <input type="file" hidden accept="image/*" onChange={handleImageChange} />
                        </IconButton>
                        <Box sx={{ flex: 1 }} />
                        <StyledButton
                          variant="contained"
                          endIcon={<SendIcon />}
                          onClick={handleCommentSubmit}
                          disabled={submitting || (!newCommentText.trim() && !newCommentImage)}
                        >
                          {submitting ? <CircularProgress size={20} color="inherit" /> : "Send"}
                        </StyledButton>
                      </Box>
                    </Box>
                  </CardContent>
                </GlassCard>
              </Fade>

              {/* Attachments - Moved here after Comments */}
              <Fade in timeout={1400}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Attachments ({taskAttachments?.length || 0})
                        </Typography>
                      </Box>
                      <Tooltip title="Add Attachments">
                        <StyledIconButton 
                          size="small"
                          onClick={() => setAttachmentOpen(true)}
                          sx={{ 
                            background: alpha(theme.palette.primary.main, 0.1),
                            "&:hover": {
                              background: alpha(theme.palette.primary.main, 0.2),
                            }
                          }}
                        >
                          <UploadFileIcon />
                        </StyledIconButton>
                      </Tooltip>
                    </Box>
                    {taskAttachments && taskAttachments.length > 0 ? (
                      <Stack spacing={1.5}>
                        {taskAttachments.map((attachment) => (
                          <Card
                            key={attachment.id}
                            sx={{
                              borderRadius: 2,
                              transition: "all 0.3s ease",
                              cursor: "pointer",
                              "&:hover": {
                                transform: "translateY(-2px)",
                                boxShadow: 4,
                              },
                            }}
                            onClick={() => window.open(attachment.url, "_blank")}
                          >
                            <CardActionArea>
                              <Box
                                sx={{
                                  p: 1.5,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1.5,
                                }}
                              >
                                {attachment.url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                                  <InsertPhotoIcon sx={{ fontSize: 28, color: theme.palette.primary.main }} />
                                ) : attachment.url.match(/\.pdf$/i) ? (
                                  <PictureAsPdfIcon sx={{ fontSize: 28, color: "#f44336" }} />
                                ) : (
                                  <DescriptionIcon sx={{ fontSize: 28, color: theme.palette.grey[500] }} />
                                )}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    flex: 1,
                                  }}
                                >
                                  {attachment.title}
                                </Typography>
                              </Box>
                            </CardActionArea>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 4,
                          textAlign: "center",
                          background: alpha(theme.palette.warning.main, 0.05),
                          borderRadius: 3,
                          border: `2px dashed ${alpha(theme.palette.warning.main, 0.2)}`,
                        }}
                      >
                        <AttachFileIcon sx={{ fontSize: 48, color: alpha(theme.palette.warning.main, 0.5), mb: 2 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          No Attachments Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Drag and drop files or click to upload
                        </Typography>
                        <StyledButton
                          variant="contained"
                          size="small"
                          startIcon={<UploadFileIcon />}
                          onClick={() => setAttachmentOpen(true)}
                        >
                          Upload Files
                        </StyledButton>
                      </Paper>
                    )}
                  </CardContent>
                </GlassCard>
              </Fade>

              {/* Reminders - Moved here after Attachments */}
              <Fade in timeout={1600}>
                <GlassCard>
                  <CardContent sx={{ p: 3 }}>
                    <SectionHeader>
                      <NotificationsNoneIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Reminders
                      </Typography>
                    </SectionHeader>
                    <FormControl fullWidth size="small">
                      <Select
                        value={task?.remindIn || ""}
                        onChange={handleReminderChange}
                        displayEmpty
                        sx={{
                          borderRadius: 2,
                          "& .MuiSelect-select": {
                            py: 1.5,
                          },
                        }}
                      >
                        <MenuItem value="">No Reminder</MenuItem>
                        <MenuItem value="ONE_HOUR">1 Hour Before</MenuItem>
                        <MenuItem value="ONE_DAY">1 Day Before</MenuItem>
                        <MenuItem value="ONE_WEEK">1 Week Before</MenuItem>
                        <MenuItem value="CUSTOM">Custom</MenuItem>
                      </Select>
                    </FormControl>
                    {task?.remindAt && (
                      <Paper
                        elevation={0}
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          background: alpha(theme.palette.warning.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Reminder set for:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.5 }}>
                          {handleDateWithTime(task.remindAt)}
                        </Typography>
                      </Paper>
                    )}
                  </CardContent>
                </GlassCard>
              </Fade>

              {/* Activity Timeline */}
              {taskHistory && taskHistory.length > 0 && (
                <Fade in timeout={1800}>
                  <GlassCard>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <TimelineIcon sx={{ color: theme.palette.primary.main }} />
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Activity Timeline
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => setActivityExpanded(!activityExpanded)}>
                          {activityExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                      <Collapse in={activityExpanded}>
                        <Box
                          sx={{
                            maxHeight: 400,
                            overflowY: "auto",
                            "&::-webkit-scrollbar": { width: 6 },
                            "&::-webkit-scrollbar-thumb": {
                              background: alpha(theme.palette.primary.main, 0.3),
                              borderRadius: 3,
                            },
                          }}
                        >
                          <Stack spacing={2}>
                            {taskHistory.map((entry, index) => {
                              const statusValue = entry.newStatus as TaskStatus;
                              return (
                                <Box
                                  key={index}
                                  sx={{
                                    pl: 2,
                                    borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                    position: "relative",
                                    "&::before": {
                                      content: '""',
                                      position: "absolute",
                                      left: -6,
                                      top: 8,
                                      width: 10,
                                      height: 10,
                                      borderRadius: "50%",
                                      backgroundColor: theme.palette.primary.main,
                                    },
                                  }}
                                >
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Status changed to{" "}
                                    <Chip
                                      label={getTaskStatusLabel(statusValue)}
                                      size="small"
                                      sx={{
                                        ml: 0.5,
                                        height: 20,
                                        fontSize: "0.75rem",
                                        backgroundColor: getTaskStatusContainerBgColor(statusValue),
                                        color: getTaskStatusContainerTextColor(statusValue),
                                      }}
                                    />
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {handleDateWithTime(entry.date.toString())}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Collapse>
                      {!activityExpanded && taskHistory.length > 0 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                          {taskHistory.length} status {taskHistory.length === 1 ? "change" : "changes"}
                        </Typography>
                      )}
                    </CardContent>
                  </GlassCard>
                </Fade>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Box>

      {/* Legacy Layout - Hidden but preserved for functionality */}
      <Box display="none">
      <Box
        component="section"
        width={{ xs: "100%", md: "56%" }}
        flexDirection="column"
        sx={{ overflow: "hidden" }}
      >
        {/* Sticky header card - Enhanced Professional */}
        <Box
          position="sticky"
          top={0}
          zIndex={10}
          sx={{
            px: 2,
            pt: 1.5,
            pb: 1,
            background:
              theme.palette.mode === "dark"
                ? alpha(theme.palette.background.paper, 0.95)
                : alpha("#ffffff", 0.98),
            backdropFilter: "saturate(180%) blur(20px)",
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            boxShadow: theme.palette.mode === "dark"
              ? "0 4px 20px rgba(0, 0, 0, 0.4)"
              : "0 4px 20px rgba(0, 0, 0, 0.08)",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{ flexWrap: { xs: "wrap", md: "nowrap" }, rowGap: 1 }}
          >
            {/* Left side: back + creator avatar + type + ref + status */}
            <Box
              display="flex"
              alignItems="center"
              gap={1.25}
              minWidth={0}
              sx={{ flexWrap: "wrap", rowGap: 0.5, flex: "1 1 auto" }}
            >
              <Tooltip title="Back">
                <IconButton onClick={() => navigate(-1)} size="small">
                  <ArrowBackIcon sx={{ color: theme.palette.primary.main }} />
                </IconButton>
              </Tooltip>

              {/* creator avatar before type/ref */}
              <Avatar
                src={task?.createdBy?.profile?.imageUrl || task?.createdBy?.imageUrl || ""}
                alt={task?.createdBy?.username}
                sx={{ width: 28, height: 28 }}
              />
              <Typography variant="body2" color="text.secondary" noWrap>
                {task?.createdBy?.username}
              </Typography>

              {/* Type before reference */}
              {task?.type && (
                <Chip
                  label={task.type}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.info.main, 0.12),
                    color: theme.palette.info.main,
                  }}
                />
              )}

              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ maxWidth: 240 }}
              >
                Ref: <strong>{task?.ref || "—"}</strong>
              </Typography>

              {/* Inline status select */}
              <Box display="flex" alignItems="center" gap={0.75}>
                <PieChartIcon fontSize="small" />
                <Select
                  size="small"
                  value={task?.status || ""}
                  onChange={handleStatusChange}
                  displayEmpty
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    background: "transparent",
                  }}
                  renderValue={(selected) =>
                    selected ? (
                      <Box
                        sx={{
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: getTaskStatusContainerBgColor(
                            selected as TaskStatus
                          ),
                          color: getTaskStatusContainerTextColor(
                            selected as TaskStatus
                          ),
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        {getTaskStatusLabel(selected as TaskStatus)}
                      </Box>
                    ) : (
                      <Typography color="text.secondary">Status</Typography>
                    )
                  }
                >
                  {statusOptions.map((s) => (
                    <MenuItem key={s} value={s}>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          backgroundColor: getTaskStatusContainerBgColor(s),
                          color: getTaskStatusContainerTextColor(s),
                          fontWeight: 600,
                        }}
                      >
                        {getTaskStatusLabel(s)}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>

            {/* Right side: action bar (wraps on small screens) */}
            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{
                flex: { xs: "1 1 100%", md: "0 0 auto" },
                justifyContent: { xs: "flex-start", md: "flex-end" },
                flexWrap: "wrap",
                rowGap: 0.5,
                mt: { xs: 0.5, md: 0 },
              }}
            >
              {/* Public toggle */}
              <Tooltip title={task?.public ? "Make private" : "Make public"}>
                <span>
                  <IconButton onClick={changePrivacy} size="small">
                    {task?.public ? (
                      <PublicIcon sx={{ color: theme.palette.primary.main }} />
                    ) : (
                      <PublicOffIcon sx={{ color: theme.palette.primary.main }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              {/* Visibility selector */}
              <Tooltip title={`Visibility: ${task?.visibility || "—"}`}>
                <span>
                  <IconButton size="small" onClick={(e) => setVisAnchorEl(e.currentTarget)}>
                    <VisibilityIcon sx={{ color: theme.palette.primary.main }} />
                  </IconButton>
                </span>
              </Tooltip>
              {task?.visibility && (
                <Chip size="small" label={task.visibility} sx={{ ml: 0.25, fontWeight: 700 }} />
              )}
              <Menu anchorEl={visAnchorEl} open={visMenuOpen} onClose={() => setVisAnchorEl(null)}>
                {VISIBILITY_OPTIONS.map((opt) => (
                  <MenuItem
                    key={opt}
                    selected={task?.visibility === opt}
                    onClick={() => updateVisibility(opt)}
                  >
                    {opt}
                  </MenuItem>
                ))}
                <MenuItem disabled divider>—</MenuItem>
                <MenuItem onClick={changePrivacy}>
                  {task?.public ? "Set non-public" : "Set public"}
                </MenuItem>
              </Menu>

              {/* Add attachments */}
              <Tooltip title="Add attachments">
                <IconButton onClick={() => setAttachmentOpen(true)} size="small">
                  <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                </IconButton>
              </Tooltip>

              {/* Add sub-task (only for parent tasks) + project membership guard */}
              {task?.parentTaskId === null && (
                <Tooltip
                  title={
                    projectMemberLoading
                      ? "Checking permissions…"
                      : isProjectMember
                      ? "Add sub-task"
                      : "You must be a member of this project to add sub-tasks"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={projectMemberLoading || !isProjectMember}
                      onClick={() => {
                        if (!isProjectMember) {
                          setSnackbar({
                            open: true,
                            message: "You are not a member of this project.",
                            severity: "warning",
                          });
                          return;
                        }
                        // pass projectId so NewTask can preselect (it will also inherit defaults)
                        const q = task?.projectId ? `?projectId=${task.projectId}` : "";
                        navigate(`/new-task/${id}${q}`);
                      }}
                    >
                      <AddTaskIcon
                        sx={{
                          color: isProjectMember
                            ? theme.palette.primary.main
                            : theme.palette.text.disabled,
                        }}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              {/* Followers icon + count */}
              <Tooltip title="Followers">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setFollowersOpen(true);
                      getFollowersList();
                    }}
                  >
                    <Badge
                      badgeContent={followersCount}
                      color={isFollowed ? "primary" : "default"}
                      overlap="circular"
                    >
                      <GroupIcon
                        sx={{
                          color: isFollowed
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                        }}
                      />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>

              {/* History */}
              <Tooltip title="Status History">
                <IconButton onClick={() => setHistoryDialogOpen(true)} size="small">
                  <HistoryIcon sx={{ color: theme.palette.primary.main }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Edit">
                <IconButton onClick={() => navigate(`/edit-task/${id}`)} size="small">
                  <EditIcon sx={{ color: theme.palette.primary.main }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete">
                <IconButton
                  onClick={() => {
                    if (task?.parentTaskId === null && (subTasks?.length || 0) > 0) {
                      setSnackbar({
                        open: true,
                        message:
                          "This task has sub-tasks. Delete them first, then delete this task.",
                        severity: "warning",
                      });
                      return;
                    }
                    setDeleteTaskOpen(true);
                  }}
                  size="small"
                >
                  <DeleteIcon sx={{ color: theme.palette.error.main }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Approvals inline strip */}
          <Box mt={1}>
            {approvalOverallLabel && (
              <Chip
                icon={approvalOverallLabel.icon}
                label={`Approval: ${approvalOverallLabel.label}${
                  task?.currentApprovalStep ? ` • Step ${task.currentApprovalStep}` : ""
                }`}
                sx={{
                  mr: 1,
                  bgcolor: alpha(approvalOverallLabel.color, 0.1),
                  color: approvalOverallLabel.color,
                  fontWeight: 700,
                }}
              />
            )}
            {loadingApprovals ? (
              <Skeleton variant="text" width={240} />
            ) : approvalsByStep.length ? (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mt={1}>
                {approvalsByStep.map(({ step, items }) => (
                  <Box
                    key={step.id}
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1.5,
                      border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
                      background: alpha(theme.palette.background.paper, 0.6),
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      Step {step.stepOrder} ({step.requiredStrategy})
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5} flexWrap="wrap">
                      {items.length ? (
                        items.map((ap) => {
                          const statusColor =
                            ap.status === "APPROVED"
                              ? theme.palette.success.main
                              : ap.status === "REJECTED"
                              ? theme.palette.error.main
                              : ap.status === "CANCELLED"
                              ? theme.palette.text.secondary
                              : theme.palette.warning.main;
                          const icon =
                            ap.status === "APPROVED" ? (
                              <CheckCircleIcon fontSize="inherit" />
                            ) : ap.status === "REJECTED" ? (
                              <DoNotDisturbOnIcon fontSize="inherit" />
                            ) : ap.status === "CANCELLED" ? (
                              <CloseIcon fontSize="inherit" />
                            ) : (
                              <PendingIcon fontSize="inherit" />
                            );
                          return (
                            <Chip
                              key={ap.id}
                              size="small"
                              variant="outlined"
                              avatar={
                                <Avatar
                                  src={ap.approver?.profile?.imageUrl || ""}
                                  alt={ap.approver?.username || ""}
                                />
                              }
                              label={`${ap.approver?.username || "—"} • ${ap.status}`}
                              sx={{
                                borderColor: alpha(statusColor, 0.6),
                                color: statusColor,
                                "& .MuiChip-avatar": { width: 20, height: 20 },
                              }}
                              icon={icon}
                            />
                          );
                        })
                      ) : (
                        <Chip size="small" label="No approvers resolved" />
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>

          {/* Title & parent preview */}
          <Box mt={1}>
            {fetchingTask ? (
              <Skeleton variant="text" width="70%" height={36} />
            ) : (
              <>
                <Tooltip
                  placement="top"
                  title={
                    <Typography sx={{ fontSize: 14, color: "#fff", p: 1, borderRadius: 1 }}>
                      {task?.title || "—"}
                    </Typography>
                  }
                >
                  {/* Expandable + RTL-aware title, aligned left */}
                  <Box
                    sx={{
                      position: "relative",
                      maxHeight: titleExpanded ? "none" : "3.4em",
                      overflow: titleExpanded ? "visible" : "hidden",
                      textAlign: "left",
                      ...(!titleExpanded && {
                        maskImage: "linear-gradient(180deg, #000 80%, transparent 100%)",
                        WebkitMaskImage: "linear-gradient(180deg, #000 80%, transparent 100%)",
                      }),
                    }}
                  >
                    <Typography
                      variant="h5"
                      fontWeight={800}
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        direction: isRTL(task?.title) ? "rtl" : "ltr",
                        textAlign: "left",
                      }}
                    >
                      {task?.title || "—"}
                    </Typography>
                  </Box>
                </Tooltip>

                {isLongTitle && (
                  <Button
                    size="small"
                    onClick={() => setTitleExpanded((v) => !v)}
                    sx={{ mt: 0.25, textTransform: "none", fontWeight: 700 }}
                  >
                    {titleExpanded ? "Show less" : "Show more"}
                  </Button>
                )}
              </>
            )}

            {/* Parent preview row under title */}
            {parentTask && (
              <Box
                mt={1}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  cursor: "pointer",
                  "&:hover .parentTitle": { textDecoration: "underline" },
                }}
                onClick={() => navigate(`/task/${parentTask.id}`)}
              >
                <Avatar
                  src={parentTask.createdBy?.profile?.imageUrl || parentTask.createdBy?.imageUrl || ""}
                  alt={parentTask.createdBy?.username}
                  sx={{ width: 22, height: 22 }}
                />
                <Typography variant="body2" className="parentTitle" sx={{ fontWeight: 700 }}>
                  {parentTask.title}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Scrollable content */}
        <Box
          flex={1}
          overflow="auto"
          p={2}
          sx={{
            "&::-webkit-scrollbar-thumb": { backgroundColor: alpha("#000", 0.2) },
          }}
        >
          {/* Description */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                Description
              </Typography>

              {fetchingTask ? (
                <>
                  <Skeleton height={12} />
                  <Skeleton height={12} width="80%" />
                  <Skeleton height={12} width="60%" />
                </>
              ) : (
                <Box
                  sx={{
                    position: "relative",
                    maxHeight: descExpanded ? "65vh" : "12.5em",
                    overflowY: descExpanded ? "auto" : "hidden",
                    pr: 1,
                    "&::-webkit-scrollbar": { width: 8 },
                    "&::-webkit-scrollbar-thumb": {
                      background: alpha("#000", 0.25),
                      borderRadius: 4,
                    },
                    ...(!descExpanded && {
                      maskImage: "linear-gradient(180deg, #000 70%, transparent 100%)",
                      WebkitMaskImage: "linear-gradient(180deg, #000 70%, transparent 100%)",
                    }),
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: 15.5,
                      lineHeight: 1.9,
                      letterSpacing: 0.1,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      textAlign: "left",
                    }}
                  >
                    {task?.description || "—"}
                  </Typography>
                </Box>
              )}

              {(task?.description?.length ?? 0) > 0 && (
                <Button
                  size="small"
                  onClick={() => setDescExpanded((v) => !v)}
                  sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
                >
                  {descExpanded ? "Show less" : "Show more"}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* ───── Progress (auto-save) — moved just after Description ───── */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                Progress
              </Typography>

              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Completion
                  </Typography>
                  <Typography variant="body2" fontWeight={800}>
                    {typeof task?.progress === "number" ? `${task.progress}%` : `${progressDraft}%`}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={2} sx={{ flexWrap: "wrap" }}>
                  <Slider
                    value={typeof task?.progress === "number" ? task.progress : progressDraft}
                    onChange={(_, val) => setProgressDraft(val as number)}
                    onChangeCommitted={(_, val) => saveProgress(val as number)}
                    step={5}
                    min={0}
                    max={100}
                    valueLabelDisplay="auto"
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                  <LinearProgress
                    variant="determinate"
                    value={typeof task?.progress === "number" ? task.progress : progressDraft}
                    sx={{ flex: 1, minWidth: 220 }}
                  />
                </Box>
                {progressSaving && (
                  <Typography variant="caption" color="text.secondary">
                    Saving…
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Meta row: Priority (editable) / Project / Account / Tags */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
              {/* Priority editable select */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  Priority
                </Typography>
                <Select
                  size="small"
                  value={task?.importance || ""}
                  onChange={handlePriorityChange}
                  displayEmpty
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    background: "transparent",
                  }}
                  renderValue={(selected) =>
                    selected ? (
                      <Chip
                        label={getImportanceContainerText(selected as TaskImportance)}
                        sx={{
                          color: "#fff",
                          backgroundColor: getImportanceContainerColor(selected as TaskImportance),
                          fontWeight: 700,
                        }}
                      />
                    ) : (
                      <Typography color="text.secondary">Priority</Typography>
                    )
                  }
                >
                  {priorityOptions.map((p) => (
                    <MenuItem key={p} value={p}>
                      <Chip
                        size="small"
                        label={getImportanceContainerText(p)}
                        sx={{
                          color: "#fff",
                          backgroundColor: getImportanceContainerColor(p),
                          fontWeight: 700,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Project */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  Project
                </Typography>
                {task?.projectId ? (
                  <Tooltip title={task?.projectName || `Project #${task.projectId}`}>
                    <Chip
                      clickable
                      onClick={() => navigate(`/projects/${task.projectId}`)}
                      label={task?.projectName || `Project #${task.projectId}`}
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        maxWidth: 240,
                        "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Chip label="No Project" />
                )}
              </Box>

              {/* Account */}
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  Account
                </Typography>
                {task?.accountId ? (
                  <Chip
                    clickable
                    onClick={() => navigate(`/accounts/${task.accountId}`)}
                    label={task.accountName || `Account #${task.accountId}`}
                    sx={{
                      backgroundColor: alpha(theme.palette.info.main, 0.12),
                      color: theme.palette.info.main,
                      fontWeight: 700,
                      maxWidth: 240,
                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  />
                ) : (
                  <Chip label="No Account" />
                )}
              </Box>

              {/* Tags */}
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" color="text.secondary" fontWeight={700}>
                  Tags
                </Typography>
                <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                  {(task?.tags || []).map((t) => (
                    <Chip key={t} size="small" label={t} onDelete={() => removeTag(t)} sx={{ fontWeight: 700 }} />
                  ))}
                  <TextField
                    size="small"
                    placeholder="Add tag"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addTag();
                      }
                    }}
                    sx={{ minWidth: 140 }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={savingTags || !tagsInput.trim()}
                    onClick={addTag}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {savingTags ? <CircularProgress size={18} /> : "Add"}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Assignees & Dates + Archived toggle */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              {/* Assignees grouped by step */}
              <Box display="flex" alignItems="center" gap={1.25} mb={1.5}>
                <PeopleIcon fontSize="small" />
                <Typography variant="subtitle2" fontWeight={800}>
                  Assignees by Step
                </Typography>
              </Box>

              {loadingAssignments ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : assignmentsByStep.length > 0 ? (
                <Box mb={2}>
                  {assignmentsByStep.map((stepGroup) => {
                    return (
                      <Box key={stepGroup.stepId} mb={2}>
                        {/* Step Header */}
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            size="small"
                            label={`Step ${stepGroup.stepOrder}`}
                            color="primary"
                            variant="outlined"
                            sx={{ fontWeight: 700 }}
                          />
                          <Typography variant="body2" color="text.secondary" fontWeight={600}>
                            {stepGroup.assignmentType === 'USER' && stepGroup.targetUser 
                              ? `Assigned to: ${stepGroup.targetUser.username}`
                              : stepGroup.assignmentType === 'POSITION' && stepGroup.targetPosition
                              ? `Position: ${stepGroup.targetPosition.name}`
                              : stepGroup.assignmentType === 'ROLE'
                              ? `Role: ${stepGroup.targetRole}`
                              : stepGroup.assignmentType
                            }
                          </Typography>
                        </Box>

                        {/* Assignees in this step */}
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                          {stepGroup.assignees.map((assignee: any, i: number) => {
                            const userStatusDisplay = getUserTaskStatusDisplay(assignee.userTaskStatus);
                            const initials = (assignee?.username || "").slice(0, 2).toUpperCase();
                            return (
                              <Tooltip
                                key={`${stepGroup.stepId}-${assignee.id}-${i}`}
                                title={
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {assignee.username}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Status: {userStatusDisplay.label}
                                    </Typography>
                                    {assignee.comment && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        Comment: {assignee.comment}
                                      </Typography>
                                    )}
                                    {assignee.actedAt && (
                                      <Typography variant="caption" display="block" color="text.secondary">
                                        Acted: {new Date(assignee.actedAt).toLocaleDateString()}
                                      </Typography>
                                    )}
                                  </Box>
                                }
                                arrow
                              >
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.75,
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: 4,
                                    border: `1px solid ${userStatusDisplay.color}`,
                                    backgroundColor: alpha(userStatusDisplay.color, 0.1),
                                  }}
                                >
                                  <Avatar
                                    src={assignee.imageUrl || undefined}
                                    alt={assignee.username}
                                    sx={{ width: 24, height: 24, fontSize: 12 }}
                                  >
                                    {!assignee.imageUrl ? initials : null}
                                  </Avatar>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: userStatusDisplay.color, fontWeight: 600 }}
                                  >
                                    {assignee.username}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : (
                <Box mb={2}>
                  {/* Fallback to current task assignees if no step data */}
                  {(task?.assignedUsers?.length ?? 0) > 0 ? (
                    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                      {task!.assignedUsers.map((au: any, i: number) => {
                        const uname = au?.user?.username || '—';
                        const img = au?.user?.profile?.imageUrl || undefined;
                        const initials = uname.slice(0, 2).toUpperCase();
                        return (
                          <Box
                            key={au?.user?.id ?? i}
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1,
                              py: 0.25,
                              borderRadius: 4,
                              border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                              backgroundColor: alpha(theme.palette.action.hover, 0.2),
                            }}
                          >
                            <Avatar src={img} alt={uname} sx={{ width: 24, height: 24, fontSize: 12 }}>
                              {!img ? initials : null}
                            </Avatar>
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                              {uname}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No step assignments found
                    </Typography>
                  )}
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {/* Dates + Remind (clickable) + Archived toggle */}
              <Box display="flex" alignItems="center" gap={4} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1.25}>
                  <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.error.main }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Start
                  </Typography>
                  <Typography variant="body2">
                    {task?.startDate ? handleDate(task.startDate) : "—"}
                  </Typography>
                </Box>

                <Box display="flex" alignItems="center" gap={1.25}>
                  <CalendarTodayIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Due
                  </Typography>
                  <Typography variant="body2">
                    {task?.endDate ? handleDate(task.endDate) : "—"}
                  </Typography>
                </Box>

                {/* Remind clickable -> custom reminder dialog */}
                <Box display="flex" alignItems="center" gap={1.25}>
                  <NotificationsNoneIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Remind
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={openReminderDialog}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {task?.remindIn === "custom" && task?.remindAt
                      ? `Custom • ${handleDateWithTime(task.remindAt)}`
                      : task?.remindIn ?? "Set reminder"}
                  </Button>
                </Box>

                {/* Archived toggle */}
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" color="text.secondary" fontWeight={700}>
                    Archived
                  </Typography>
                  <Switch
                    checked={!!task?.archived}
                    onChange={(_, ch) => handleArchivedToggle(ch)}
                    disabled={archivedSaving}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
          {/* Sub-tasks (multiple views) — HIDE if this is a sub-task */}
          {task?.parentTaskId === null && (
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                  gap={1}
                  sx={{ flexWrap: "wrap", rowGap: 1 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <FormatListNumberedIcon fontSize="small" />
                    <Typography variant="subtitle2" fontWeight={800}>
                      Sub-Tasks
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} sx={{ flexWrap: "wrap", rowGap: 1 }}>
                    {/* Sort control */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="sub-sort-label">Sort by</InputLabel>
                      <Select
                        labelId="sub-sort-label"
                        label="Sort by"
                        value={subTaskSort}
                        onChange={(e) => setSubTaskSort(e.target.value as SubTaskSort)}
                      >
                        <MenuItem value="createdDesc">Created ↓</MenuItem>
                        <MenuItem value="createdAsc">Created ↑</MenuItem>
                        <MenuItem value="updatedDesc">Updated ↓</MenuItem>
                        <MenuItem value="updatedAsc">Updated ↑</MenuItem>
                      </Select>
                    </FormControl>

                    {/* Status filter (multi) */}
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel id="sub-status-label">Status</InputLabel>
                      <Select
                        multiple
                        labelId="sub-status-label"
                        label="Status"
                        value={statusFilter}
                        onChange={(e) =>
                          setStatusFilter(
                            typeof e.target.value === "string"
                              ? (e.target.value.split(",") as TaskStatus[])
                              : (e.target.value as TaskStatus[])
                          )
                        }
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((s) => (
                              <Chip
                                key={s}
                                size="small"
                                label={getTaskStatusLabel(s)}
                                sx={{
                                  backgroundColor: getTaskStatusContainerBgColor(s),
                                  color: getTaskStatusContainerTextColor(s),
                                  fontWeight: 700,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {statusOptions.map((s) => (
                          <MenuItem key={s} value={s}>
                            <Checkbox checked={statusFilter.indexOf(s) > -1} />
                            <ListItemText primary={getTaskStatusLabel(s)} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Priority filter (multi) */}
                    <FormControl size="small" sx={{ minWidth: 170 }}>
                      <InputLabel id="sub-priority-label">Priority</InputLabel>
                      <Select
                        multiple
                        labelId="sub-priority-label"
                        label="Priority"
                        value={priorityFilter}
                        onChange={(e) =>
                          setPriorityFilter(
                            typeof e.target.value === "string"
                              ? (e.target.value.split(",") as TaskImportance[])
                              : (e.target.value as TaskImportance[])
                          )
                        }
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((p) => (
                              <Chip
                                key={p}
                                size="small"
                                label={getImportanceContainerText(p)}
                                sx={{
                                  color: "#fff",
                                  backgroundColor: getImportanceContainerColor(p),
                                  fontWeight: 700,
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      >
                        {priorityOptions.map((p) => (
                          <MenuItem key={p} value={p}>
                            <Checkbox checked={priorityFilter.indexOf(p) > -1} />
                            <ListItemText primary={getImportanceContainerText(p)} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <ToggleButtonGroup
                      size="small"
                      value={subTaskView}
                      exclusive
                      onChange={(_, v) => v && setSubTaskView(v)}
                    >
                      <ToggleButton value="grid">
                        <Tooltip title="Grid">
                          <ViewModuleOutlinedIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="list">
                        <Tooltip title="List">
                          <ViewListOutlinedIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value="board">
                        <Tooltip title="Board">
                          <ViewKanbanOutlinedIcon fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>

                    <Tooltip title="Number of sub-tasks after filters">
                      <Chip size="small" label={`Filtered: ${filteredSubtasks.length}`} sx={{ fontWeight: 700 }} />
                    </Tooltip>

                    <IconButton size="small" onClick={() => setSubTasksExpanded((v) => !v)}>
                      {subTasksExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </Box>

                <Collapse in={subTasksExpanded} timeout="auto" unmountOnExit>
                  {/* Views */}
                  {subTaskView === "grid" && (
                    <Box display="flex" flexWrap="wrap" gap={1.5} p={0.5}>
                      {sortedSubtasks.map((t) => (
                        <Card
                          key={t.id}
                          onClick={() => navigate(`/task/${t.id}`)}
                          sx={{
                            width: 300,
                            cursor: "pointer",
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                            background: alpha(theme.palette.background.paper, 0.6),
                            transition: "transform .18s ease, box-shadow .18s ease",
                            "&:hover": { transform: "translateY(-2px)", boxShadow: theme.shadows[4] },
                          }}
                        >
                          <CardContent sx={{ p: 1.25 }}>
                            {/* top row: avatar + title + status */}
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.75}>
                              <Box display="flex" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                                <Avatar
                                  src={t.createdBy?.profile?.imageUrl || ""}
                                  alt={t.createdBy?.username || ""}
                                  sx={{ width: 24, height: 24 }}
                                />
                                {/* small priority dot */}
                                {t.importance && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: getImportanceContainerColor(t.importance),
                                    }}
                                  />
                                )}
                                <Typography
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 700,
                                    fontSize: 14,
                                    maxWidth: 160,
                                    ...leftAlignedSingleLine(t.title),
                                  }}
                                  title={t.title}
                                >
                                  {t.title}
                                </Typography>
                              </Box>

                              <Box
                                sx={{
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: 1,
                                  fontSize: 11,
                                  fontWeight: 700,
                                  backgroundColor: getTaskStatusContainerBgColor(t.status as TaskStatus),
                                  color: getTaskStatusContainerTextColor(t.status as TaskStatus),
                                }}
                              >
                                {getTaskStatusLabel(t.status as TaskStatus)}
                              </Box>
                            </Box>

                            {/* description (2 lines) */}
                            <Typography
                              sx={{
                                color: "text.secondary",
                                fontSize: 13,
                                minHeight: 36,
                                ...leftAlignedClamp(2, t.description || ""),
                              }}
                            >
                              {t.description}
                            </Typography>

                            {/* footer: project + date */}
                            <Box mt={1} display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                              <Chip size="small" label={t.project?.name || "No Project"} sx={{ maxWidth: 160 }} />
                              <Typography variant="caption" color="text.secondary">
                                {t.endDate ? handleDate(t.endDate) : t.startDate ? handleDate(t.startDate) : "—"}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                      {!sortedSubtasks.length && <Typography variant="body2">No sub-tasks.</Typography>}
                    </Box>
                  )}

                  {subTaskView === "list" && (
                    <List dense disablePadding>
                      {sortedSubtasks.map((t) => (
                        <ListItem
                          key={t.id}
                          onClick={() => navigate(`/task/${t.id}`)}
                          sx={{
                            px: 1,
                            borderRadius: 1,
                            "&:hover": { background: alpha(theme.palette.primary.main, 0.06) },
                            cursor: "pointer",
                          }}
                          secondaryAction={
                            <Chip
                              size="small"
                              label={getTaskStatusLabel(t.status as TaskStatus)}
                              sx={{
                                borderRadius: 1,
                                fontWeight: 700,
                                backgroundColor: getTaskStatusContainerBgColor(t.status as TaskStatus),
                                color: getTaskStatusContainerTextColor(t.status as TaskStatus),
                              }}
                            />
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={t.createdBy?.profile?.imageUrl || ""}
                              alt={t.createdBy?.username || ""}
                              sx={{ width: 28, height: 28, fontSize: 13 }}
                            >
                              {(t?.title || "?").slice(0, 1).toUpperCase()}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primaryTypographyProps={{ fontWeight: 700, sx: { textAlign: "left" } }}
                            primary={
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  minWidth: 0,
                                  width: "100%",
                                }}
                              >
                                {/* priority dot */}
                                {t.importance && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      bgcolor: getImportanceContainerColor(t.importance),
                                      flex: "0 0 auto",
                                    }}
                                  />
                                )}
                                <span
                                  style={
                                    {
                                      ...leftAlignedSingleLine(t.title),
                                      width: "100%",
                                      display: "inline-block",
                                    } as React.CSSProperties
                                  }
                                >
                                  {t.title}
                                </span>
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ display: "inline-flex", gap: 1, mt: 0.25 }}>
                                <Chip size="small" label={t.project?.name || "No Project"} sx={{ height: 22 }} />
                                <Typography component="span" variant="caption" color="text.secondary">
                                  {t.endDate ? handleDate(t.endDate) : t.startDate ? handleDate(t.startDate) : "—"}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                      {!sortedSubtasks.length && (
                        <Typography variant="body2" sx={{ px: 1, py: 0.5 }}>
                          No sub-tasks.
                        </Typography>
                      )}
                    </List>
                  )}

                  {subTaskView === "board" && (
                    <Box sx={{ display: "flex", gap: 1.5, overflowX: "auto", p: 0.5 }}>
                      {statusOptions.map((col) => {
                        const items = (groupedSubtasksByStatus[col] || []) as SubtaskExtra[];
                        return (
                          <Paper
                            key={col}
                            variant="outlined"
                            sx={{
                              minWidth: 280,
                              maxWidth: 340,
                              borderRadius: 2,
                              p: 1,
                              background: alpha(theme.palette.background.paper, 0.6),
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                              }}
                            >
                              <Typography variant="subtitle2" fontWeight={800}>
                                {getTaskStatusLabel(col)}
                              </Typography>
                              <Chip size="small" label={items.length} />
                            </Box>
                            <Box display="flex" flexDirection="column" gap={1}>
                              {items
                                .slice()
                                .sort((a, b) => b.id - a.id)
                                .map((t) => (
                                  <Card
                                    key={t.id}
                                    onClick={() => navigate(`/task/${t.id}`)}
                                    sx={{
                                      borderRadius: 2,
                                      cursor: "pointer",
                                      p: 1,
                                      border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                                      "&:hover": { boxShadow: theme.shadows[3] },
                                    }}
                                  >
                                    <Box display="flex" alignItems="center" gap={0.75} mb={0.5}>
                                      <Avatar
                                        src={t.createdBy?.profile?.imageUrl || ""}
                                        alt={t.createdBy?.username || ""}
                                        sx={{ width: 22, height: 22 }}
                                      />
                                      {/* priority dot */}
                                      {t.importance && (
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: getImportanceContainerColor(t.importance),
                                          }}
                                        />
                                      )}
                                      <Typography
                                        sx={{
                                          fontSize: 14,
                                          fontWeight: 700,
                                          color: theme.palette.primary.main,
                                          ...leftAlignedSingleLine(t.title),
                                        }}
                                        title={t.title}
                                      >
                                        {t.title}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ ...leftAlignedClamp(2, t.description || "") }}
                                    >
                                      {t.description}
                                    </Typography>
                                    <Box
                                      mt={0.75}
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      gap={1}
                                    >
                                      <Chip size="small" label={t.project?.name || "No Project"} sx={{ height: 22 }} />
                                      <Typography variant="caption" color="text.secondary">
                                        {t.endDate
                                          ? handleDate(t.endDate)
                                          : t.startDate
                                          ? handleDate(t.startDate)
                                          : "—"}
                                      </Typography>
                                    </Box>
                                  </Card>
                                ))}
                              {!items.length && (
                                <Typography variant="caption" color="text.secondary">
                                  No items
                                </Typography>
                              )}
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* Advanced (estimate/spent/points) — Progress moved out */}
          <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={800} gutterBottom>
                Advanced
              </Typography>

              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1.25}>
                  <HourglassBottomIcon fontSize="small" />
                  <TextField
                    size="small"
                    label="Estimate (min)"
                    type="number"
                    value={estimateMinutesDraft}
                    onChange={(e) =>
                      setEstimateMinutesDraft(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    sx={{ minWidth: 160 }}
                    inputProps={{ min: 0 }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1.25}>
                  <TimelapseIcon fontSize="small" />
                  <TextField
                    size="small"
                    label="Spent (min)"
                    type="number"
                    value={spentMinutesDraft}
                    onChange={(e) =>
                      setSpentMinutesDraft(e.target.value === "" ? "" : Number(e.target.value))
                    }
                    sx={{ minWidth: 160 }}
                    inputProps={{ min: 0 }}
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1.25}>
                  <StarOutlineIcon fontSize="small" />
                  <TextField
                    size="small"
                    label="Points"
                    type="number"
                    value={pointsDraft}
                    onChange={(e) => setPointsDraft(e.target.value === "" ? "" : Number(e.target.value))}
                    sx={{ minWidth: 120 }}
                    inputProps={{ min: 0 }}
                  />
                </Box>
                <Button
                  variant="contained"
                  onClick={saveAdvancedFields}
                  disabled={savingAdvanced}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {savingAdvanced ? <CircularProgress size={18} color="inherit" /> : "Save"}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Attachments carousel */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <AttachFileIcon sx={{ color: theme.palette.primary.main }} />
                <Typography variant="subtitle2" fontWeight={800}>
                  Task Attachments
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <IconButton onClick={() => scroll("left")} size="small">
                  <ArrowBackIosIcon fontSize="inherit" />
                </IconButton>

                <Box
                  ref={scrollRef}
                  sx={{
                    display: "flex",
                    gap: 2,
                    overflowX: "auto",
                    scrollBehavior: "smooth",
                    px: 0.5,
                    py: 0.5,
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {taskAttachments?.map((a) => {
                    const ext = getFileType(a.url);
                    const image = isImage(ext);
                    return (
                      <Card
                        key={a.id}
                        sx={{
                          width: 140,
                          height: 140,
                          position: "relative",
                          borderRadius: 2,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.6)}`,
                          flexShrink: 0,
                          overflow: "hidden",
                        }}
                      >
                        {/* delete */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAttachmentToDelete(a);
                          }}
                          sx={{
                            position: "absolute",
                            right: 4,
                            top: 4,
                            zIndex: 2,
                            color: theme.palette.error.main,
                            background: alpha(theme.palette.background.paper, 0.8),
                            "&:hover": { background: alpha(theme.palette.error.light, 0.14) },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>

                        <CardActionArea
                          sx={{ width: "100%", height: "100%" }}
                          onClick={() => handleAttachmentCardClick(a)}
                        >
                          <Box
                            sx={{
                              height: "60%",
                              width: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: alpha(theme.palette.background.default, 0.6),
                            }}
                          >
                            {image ? (
                              <img
                                src={a.url}
                                alt={a.title}
                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                              />
                            ) : ext === "pdf" ? (
                              <PictureAsPdfIcon sx={{ fontSize: 60 }} />
                            ) : (
                              <DescriptionIcon sx={{ fontSize: 60 }} />
                            )}
                          </Box>
                          <CardContent sx={{ p: 0.5, textAlign: "center", height: "40%" }}>
                            <Typography variant="body2" noWrap title={a.title}>
                              {a.title || "Untitled"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.primary.main }}>
                              .{ext}
                            </Typography>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    );
                  })}
                  {!taskAttachments?.length && <Typography variant="body2">No files.</Typography>}
                </Box>

                <IconButton onClick={() => scroll("right")} size="small">
                  <ArrowForwardIosIcon fontSize="inherit" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Right column: comments */}
      <Box
        component="aside"
        width={{ xs: "100%", md: "44%" }}
        display="flex"
        flexDirection="column"
        sx={{ height: "100%" }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            background: theme.palette.mode === "dark" ? theme.palette.background.paper : alpha("#fff", 0.9),
            backdropFilter: "saturate(160%) blur(6px)",
          }}
        >
          <Typography variant="subtitle1" fontWeight={800}>
            Comments
          </Typography>
        </Box>

        <Box
          flex={1}
          overflow="auto"
          sx={{
            p: 2,
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": { background: alpha("#000", 0.2), borderRadius: 4 },
          }}
        >
          {fetchingComments ? (
            <>
              {[0, 1, 2].map((k) => (
                <Card key={k} sx={{ mb: 1.5, borderRadius: 3 }}>
                  <CardContent sx={{ display: "flex", gap: 1.5 }}>
                    <Skeleton variant="circular" width={44} height={44} />
                    <Box flex={1}>
                      <Skeleton variant="text" width="40%" />
                      <Skeleton variant="text" />
                      <Skeleton variant="rectangular" height={120} />
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            comments.map((c) => (
              <Card
                key={c.id}
                sx={{
                  mb: 1.75,
                  borderRadius: 3,
                  borderLeft: `4px solid ${alpha(theme.palette.primary.main, 0.8)}`,
                  background:
                    theme.palette.mode === "dark"
                      ? alpha(theme.palette.primary.main, 0.06)
                      : alpha("#1976d2", 0.05),
                  boxShadow: theme.shadows[2],
                }}
              >
                <CardContent sx={{ p: 1.75 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1.25}>
                      <Avatar
                        src={c.user.imageUrl || c.user.profile?.imageUrl || ""}
                        alt={c.user.username}
                        sx={{ width: 44, height: 44 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={800}>
                          {c.user.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {handleDateWithTime(c.date)}
                        </Typography>
                      </Box>
                    </Box>

                    {(c.user.id === userId || role === "admin") && (
                      <Tooltip title="Delete comment">
                        <IconButton color="error" size="small" onClick={() => handleCommentDeleteClick(c.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  {c.text && (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.25,
                        borderRadius: 2,
                        background: theme.palette.mode === "dark" ? alpha("#fff", 0.04) : alpha("#000", 0.035),
                        border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                        mb: c.imageUrl ? 1 : 0,
                      }}
                    >
                      <Typography variant="body1" sx={{ fontSize: 15, lineHeight: 1.7 }}>
                        {c.text}
                      </Typography>
                    </Paper>
                  )}

                  {c.imageUrl && (
                    <>
                      <Box sx={{ mt: 0.5 }}>
                        <img
                          src={c.imageUrl}
                          alt="Comment"
                          style={{
                            width: 260,
                            height: 260,
                            objectFit: "cover",
                            borderRadius: 10,
                            cursor: "pointer",
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 2px 12px rgba(0,0,0,.35)"
                                : "0 2px 12px rgba(0,0,0,.12)",
                          }}
                          onClick={() => setOpenImageCommentId(c.id)}
                        />
                      </Box>

                      <Dialog
                        open={openImageCommentId === c.id}
                        onClose={() => setOpenImageCommentId(null)}
                        maxWidth="md"
                        fullWidth
                        slots={{ backdrop: Backdrop }}
                        slotProps={{
                          backdrop: {
                            sx: { backdropFilter: "blur(10px)", backgroundColor: "rgba(0,0,0,0.3)" },
                          },
                        }}
                      >
                        <DialogContent sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
                          <img src={c.imageUrl} alt="Full" style={{ width: "100%", height: "auto", borderRadius: 8 }} />
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </Box>

        {/* Composer bar */}
        <Box
          sx={{
            p: 1.25,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
            background: theme.palette.mode === "dark" ? theme.palette.background.paper : alpha("#fff", 0.9),
          }}
        >
          <Box display="flex" alignItems="flex-end" gap={1.25} sx={{ flexWrap: "wrap" }}>
            {/* multiline + Shift+Enter for new line */}
            <TextField
              fullWidth
              size="small"
              placeholder="Write a comment…"
              value={newCommentText}
              multiline
              minRows={1}
              maxRows={6}
              onChange={(e) => setNewCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleCommentSubmit();
                }
              }}
              sx={{
                "& .MuiInputBase-root": {
                  borderRadius: 2,
                  background: theme.palette.mode === "dark" ? alpha("#fff", 0.035) : alpha("#000", 0.03),
                },
              }}
            />
            <Tooltip title="Attach image">
              <IconButton component="label">
                <InsertPhotoIcon />
                <input type="file" hidden onChange={handleImageChange} />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              onClick={handleCommentSubmit}
              disabled={submitting}
              sx={{ minWidth: 120, fontWeight: 700, textTransform: "none" }}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : "Send"}
            </Button>
          </Box>

          {newCommentImageSrc && (
            <Box display="flex" alignItems="center" gap={1.25} mt={1}>
              <img
                src={newCommentImageSrc}
                alt="preview"
                style={{ width: 68, height: 68, objectFit: "cover", borderRadius: 10 }}
              />
              <Button variant="text" color="error" onClick={handleImageCancel}>
                Remove image
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Confirmations & Modals */}
      {/* Delete Task */}
      <Dialog open={deleteTaskOpen} onClose={() => setDeleteTaskOpen(false)}>
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this task?</DialogContentText>
          {task?.parentTaskId === null && (subTasks?.length || 0) > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              This task has sub-tasks. Delete them first, then delete this task.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTaskOpen(false)}>Cancel</Button>
          <Button
            onClick={deleteTask}
            color="error"
            variant="contained"
            disabled={task?.parentTaskId === null && (subTasks?.length || 0) > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Comment */}
      <Dialog open={commentOpen} onClose={() => setCommentOpen(false)}>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete this comment?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCommentOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmCommentDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Attachments */}
      <Dialog
        open={attachmentOpen}
        onClose={() => {
          setAttachmentOpen(false);
          setNewAttachments([]);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ backgroundColor: "#5DB0FF", color: "white" }}>
          Upload Files
        </DialogTitle>
        <DialogContent>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
            sx={{ my: 2, borderColor: "#5DB0FF", color: "#5DB0FF" }}
          >
            Choose Files
            <input type="file" hidden multiple onChange={handleAttachmentFileChange} />
          </Button>

          {newAttachments.map((fileObj, index) => (
            <Box key={index} sx={{ my: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Box>
                {fileObj.file.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(fileObj.file)}
                    alt="preview"
                    style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6 }}
                  />
                ) : (
                  <DescriptionIcon sx={{ fontSize: 44 }} />
                )}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="body2" fontWeight={700}>
                  {fileObj.file.name}
                </Typography>
                <TextField
                  fullWidth
                  label="Title"
                  value={fileObj.title}
                  onChange={(e) => handleAttachmentTitleChange(index, e.target.value)}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAttachmentOpen(false);
              setNewAttachments([]);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#5DB0FF", minWidth: 100 }}
            onClick={handleAttachmentSend}
            disabled={!newAttachments.length || addingAttachments}
          >
            {addingAttachments ? <CircularProgress size={22} color="inherit" /> : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Image Attachment */}
      <Dialog
        open={!!selectedAttachmentImageUrl}
        onClose={() => setSelectedAttachmentImageUrl(null)}
        maxWidth="md"
        fullWidth
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: { sx: { backdropFilter: "blur(10px)", backgroundColor: "rgba(0,0,0,0.3)" } },
        }}
      >
        <DialogContent sx={{ backgroundColor: "transparent", boxShadow: "none" }}>
          {selectedAttachmentImageUrl && (
            <img src={selectedAttachmentImageUrl} alt="Full preview" style={{ width: "100%", height: "auto", borderRadius: 8 }} />
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Attachment */}
      <Dialog open={!!attachmentToDelete} onClose={() => setAttachmentToDelete(undefined)}>
        <DialogTitle>Delete Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{attachmentToDelete?.title || "this attachment"}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttachmentToDelete(undefined)}>Cancel</Button>
          <Button
            onClick={() => attachmentToDelete && handleDeleteAttachment(attachmentToDelete.id)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status History Dialog */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Status History</DialogTitle>
        <DialogContent dividers>
          {taskHistory?.length ? (
            <Box display="flex" flexDirection="column" gap={1}>
              {taskHistory!.map((h) => (
                <Card key={h.id} variant="outlined" sx={{ borderRadius: 2, background: alpha("#5DB0FF", 0.05) }}>
                  <CardContent sx={{ p: 1.25 }}>
                    <Typography sx={{ color: "#5DB0FF", fontWeight: 700, mb: 0.25 }}>{h.newStatus}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(h.date).toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography variant="body2">No history.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Followers Dialog */}
      <Dialog open={followersOpen} onClose={() => setFollowersOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Followers</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
            <Chip label={`${followersCount} follower${followersCount === 1 ? "" : "s"}`} />
            <Button
              size="small"
              variant={isFollowed ? "outlined" : "contained"}
              onClick={handleFollowToggle}
              startIcon={isFollowed ? <PersonOutlineIcon /> : <PersonIcon />}
              disabled={isCheckingFollow}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              {isFollowed ? "Unfollow" : "Follow"}
            </Button>
          </Box>

          {followersLoading ? (
            <>
              {[0, 1, 2].map((k) => (
                <Box key={k} display="flex" alignItems="center" gap={1} mb={1}>
                  <Skeleton variant="circular" width={36} height={36} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Box>
              ))}
            </>
          ) : followers.length ? (
            <List dense>
              {followers.map((u) => (
                <ListItem key={u.id}>
                  <ListItemAvatar>
                    <Avatar src={u.imageUrl || ""} alt={u.username} sx={{ width: 36, height: 36 }} />
                  </ListItemAvatar>
                  <ListItemText
                    primaryTypographyProps={{ fontWeight: 700 }}
                    primary={u.name || u.username}
                    secondary={u.name ? `@${u.username}` : undefined}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2">No followers yet.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFollowersOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Custom Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onClose={() => setReminderDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Custom Reminder</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="datetime-local"
            label="Reminder time"
            value={reminderDatetime}
            onChange={(e) => setReminderDatetime(e.target.value)}
            sx={{ mt: 1 }}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="caption" color="text.secondary">
            This will set <strong>remindIn = "custom"</strong> and store your selected date/time.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReminderDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveCustomReminder} disabled={savingReminder || !reminderDatetime} variant="contained">
            {savingReminder ? <CircularProgress size={18} color="inherit" /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbar((p) => ({ ...p, open: false }))} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
      {/* End of legacy hidden layout */}

      {/* Visibility Menu */}
      <Menu anchorEl={visAnchorEl} open={visMenuOpen} onClose={() => setVisAnchorEl(null)}>
        {VISIBILITY_OPTIONS.map((opt) => (
          <MenuItem
            key={opt}
            selected={task?.visibility === opt}
            onClick={() => updateVisibility(opt)}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      {/* Status Change Comment Dialog */}
      <Dialog
        open={statusChangeDialogOpen}
        onClose={cancelStatusChange}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DescriptionIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Update Task Status
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2, color: "text.secondary" }}>
            Add a comment or notes about this status change (optional):
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            rows={4}
            fullWidth
            placeholder="Enter your comment or notes..."
            value={statusChangeComment}
            onChange={(e) => setStatusChangeComment(e.target.value)}
            variant="outlined"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
              },
            }}
          />
          {pendingStatusChange && (
            <Box sx={{ mt: 2, p: 1.5, borderRadius: 1.5, backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.primary.main, display: "block", mb: 0.5 }}>
                New Status:
              </Typography>
              <Chip
                label={getUserTaskStatusDisplay(pendingStatusChange.newStatus).label}
                size="small"
                icon={getUserTaskStatusDisplay(pendingStatusChange.newStatus).icon}
                sx={{
                  backgroundColor: alpha(getUserTaskStatusDisplay(pendingStatusChange.newStatus).color, 0.15),
                  color: getUserTaskStatusDisplay(pendingStatusChange.newStatus).color,
                  fontWeight: 600,
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={cancelStatusChange}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmStatusChange}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
              },
            }}
          >
            Save Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <FloatingActionButton
        id="quick-action-button"
        onClick={(e) => setQuickActionAnchor(e.currentTarget)}
      >
        <SpeedIcon />
      </FloatingActionButton>

      {/* Quick Action Menu */}
      <Menu
        anchorEl={quickActionAnchor}
        open={quickActionOpen}
        onClose={() => setQuickActionAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 220,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <MenuItem onClick={() => navigate(`/edit-task/${id}`)}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <EditIcon fontSize="small" color="primary" />
            <Typography variant="body2">Edit Task</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handleCopyLink}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <ContentCopyIcon fontSize="small" color="primary" />
            <Typography variant="body2">Copy Link</Typography>
          </Stack>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <PrintIcon fontSize="small" color="primary" />
            <Typography variant="body2">Print Task</Typography>
          </Stack>
        </MenuItem>
        <Divider />
        <MenuItem disabled>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            QUICK STATUS CHANGE
          </Typography>
        </MenuItem>
        {statusOptions.map((status) => (
          <MenuItem
            key={status}
            onClick={() => handleQuickStatusChange(status)}
            selected={task?.status === status}
          >
            <Chip
              label={getTaskStatusLabel(status)}
              size="small"
              sx={{
                backgroundColor: getTaskStatusContainerBgColor(status),
                color: getTaskStatusContainerTextColor(status),
                fontWeight: 600,
              }}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => setShowKeyboardShortcuts(true)}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <KeyboardIcon fontSize="small" color="primary" />
            <Typography variant="body2">Keyboard Shortcuts</Typography>
          </Stack>
        </MenuItem>
      </Menu>

      {/* Keyboard Shortcuts Dialog */}
      <Dialog
        open={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
          <KeyboardIcon color="primary" />
          Keyboard Shortcuts
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {[
              { key: "E", action: "Edit task" },
              { key: "C", action: "Focus comment input" },
              { key: "S", action: "Quick actions menu" },
              { key: "A", action: "Add attachment" },
              { key: "Esc", action: "Go back" },
              { key: "?", action: "Show this dialog" },
            ].map(({ key, action }) => (
              <Paper
                key={key}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: alpha(theme.palette.primary.main, 0.05),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2">{action}</Typography>
                <Chip
                  label={key}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontFamily: "monospace",
                    bgcolor: theme.palette.background.paper,
                    border: `2px solid ${theme.palette.divider}`,
                  }}
                />
              </Paper>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowKeyboardShortcuts(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskDetails;
